import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus, Trash2, User, Anchor, Box, Download, Save, Droplets, Map as MapIcon,
  Loader2, Database, Calendar, Users, CheckCircle2, Circle, Plane, Flame,
  CheckSquare, PackageCheck, Backpack, Scale, X, Stethoscope, Wrench, Utensils,
  Ship, Printer, FileJson, Coffee, ShoppingBag, List, LayoutList, ChevronDown,
  ChevronUp, DollarSign, ArrowRight, Calculator, Shield, Clipboard, AlertTriangle,
  Menu, X as CloseIcon, Tent, Sunrise, Sunset, Wind, Bus, Bed, Eye, PieChart,
  ArrowLeftRight, Check, Split, Settings, Upload, FileText, LogOut, Lock, KeyRound,
  ExternalLink, Phone, MapPin, Info, Globe, Clock, Navigation, Cloud, Thermometer, RefreshCw,
  Edit2, SaveAll, Filter, ChevronRight
} from 'lucide-react';

// Firebase Imports
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signOut
} from 'firebase/auth';
import {
    getFirestore, collection, doc, addDoc, updateDoc, deleteDoc,
    onSnapshot, writeBatch, setDoc, arrayUnion, arrayRemove, getDocs
} from 'firebase/firestore';

// Safe number formatting helper
const safeFixed = (value, decimals = 1) => {
    const num = Number(value);
    return (!isNaN(num) && isFinite(num)) ? num.toFixed(decimals) : '0.0';
};

// --- Firebase Initialization ---
const productionConfig = {
    apiKey: "AIzaSyCrF1YUJLa1s6Q-sDqmZbRdrsLRi3-a_t8",
    authDomain: "mountainrivercanoe.firebaseapp.com",
    projectId: "mountainrivercanoe",
    storageBucket: "mountainrivercanoe.firebasestorage.app",
    messagingSenderId: "933586827228",
    appId: "1:933586827228:web:96d5dab7f46fcd86e48119"
};
const firebaseConfig = typeof __firebase_config !== 'undefined' && __firebase_config
    ? JSON.parse(__firebase_config)
    : productionConfig;
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'mountainrivercanoe';

// --- Global Constants ---
const MAX_PROPOSALS_PER_PERSON = 3;
const DEFAULT_TRIP_DURATION = 15;
const DEFAULT_FOOD_LBS_PER_DAY = 2.0;
const SECRET_INVITE_CODE = "River2026";

// --- Weather Locations Configuration ---
const WEATHER_LOCATIONS = [
  { name: 'Yellowknife', code: 'YZF', lat: 62.46, lon: -114.44, navCanada: 'https://flightplanning.navcanada.ca/cgi-bin/C_AWIS/C_awis_view.cgi?Airport=CYZF' },
  { name: 'Norman Wells', code: 'YWY', lat: 65.28, lon: -126.80, navCanada: 'https://flightplanning.navcanada.ca/cgi-bin/C_AWIS/C_awis_view.cgi?Airport=CYVQ' },
  { name: 'Norseman Lake', code: 'NORS', lat: 64.63, lon: -127.33, navCanada: null },
  { name: 'Dot Lake', code: 'CEU8', lat: 65.26, lon: -126.68, navCanada: null }
];

// --- Standard Data Definitions ---
const standardPersonalKit = {
    "Paddling Gear": ["Drysuit / Paddle Jacket", "Neoprene Boots", "River Gloves", "Helmet (if req)", "Throw Bag (Personal)"],
    "Shelter": ["Tent / Hammock", "Ground Sheet", "Stakes & Guylines"],
    "Sleeping": ["Sleeping Bag", "Sleeping Pad", "Pillow/Stuff Sack"],
    "Head": ["Sun Hat", "Warm Toque", "Buff/Neck Gaiter", "Sunglasses"],
    "Hands": ["Warm Mitts (Camp)", "Spare Gloves"],
    "Upper Body": ["Base Layer", "Mid Layer (Fleece)", "Insulated Jacket", "Rain/Wind Shell", "Camp T-Shirt"],
    "Lower Body": ["Underwear (x3)", "Long Johns", "Quick Dry Pants", "Shorts/Swim Trunk", "Rain Pants"],
    "Feet": ["Hiking Socks", "Warm Sleep Socks", "Camp Shoes"],
    "Hygiene & Meds": ["Toothbrush & Paste", "Biodegradable Soap", "Personal Meds", "Sunscreen & Lip Balm", "Glasses/Contacts"],
    "Tech": ["Phone", "Power Bank & Cables", "Camera", "Batteries"],
    "Camp Essentials": ["Headlamp", "Water Bottle", "Camp Towel", "Dry Bag", "Book/Journal", "Bear Spray"]
};

const standardPersonalMenu = {
    "Breakfast": ["Oatmeal/Granola", "Powdered Milk", "Coffee/Tea", "Sugar/Creamer", "Breakfast Bars"],
    "Lunch": ["Tortillas/Bagels", "Hard Cheese", "Salami/Pepperoni", "Tuna Packets", "PB & J", "Energy Bars"],
    "Dinner": ["Dehydrated Meal", "Instant Mashed/Rice", "Ramen", "Mac & Cheese", "Couscous"],
    "Dessert": ["Chocolate Bar", "Cookies", "Pudding", "Marshmallows"],
    "Snacks": ["Trail Mix", "Beef Jerky", "Dried Fruit", "Pepperoni Sticks", "Pretzels", "Candy"],
    "Drinks": ["Electrolyte Powder", "Hot Chocolate", "Whisky/Spirits", "Cider", "Tea"]
};

const standardGroupFood = {
    "Breakfast": ["Pancake Mix", "Syrup", "Oatmeal (Bulk)", "Brown Sugar", "Coffee (Bulk)", "Tea", "Bacon", "Eggs"],
    "Lunch": ["Tortillas", "Bagels", "Cheese Block", "Summer Sausage", "Peanut Butter", "Jam", "Tuna Pouch", "Crackers"],
    "Dinner": ["Chili", "Pasta", "Pasta Sauce", "Rice", "Beans", "Shepherd's Pie", "Stir Fry Veggies", "Curry", "Mashed Potatoes"],
    "Dessert": ["Trail Mix", "Chocolate", "Cookies", "Marshmallows", "Popcorn", "Beef Jerky"],
    "Staples/Condiments": ["Oil", "Butter/Ghee", "Salt & Pepper", "Spice Kit", "Hot Sauce", "Soy Sauce", "Sugar", "Flour"]
};

// --- Helper Components ---

const ProgressBar = ({ value, max, colorClass }) => {
    const percentage = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
    return (
        <div className="w-full bg-slate-200 rounded-full h-2.5 mt-2 overflow-hidden">
            <div className={`h-2.5 rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${percentage}%` }}></div>
        </div>
    );
};

const StatCard = ({ icon: Icon, title, value, subtext, color = "blue" }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        orange: 'bg-orange-50 text-orange-600',
        pink: 'bg-pink-50 text-pink-600',
        emerald: 'bg-emerald-50 text-emerald-600',
        red: 'bg-red-50 text-red-600',
        green: 'bg-green-50 text-green-600',
        indigo: 'bg-indigo-50 text-indigo-600',
        purple: 'bg-purple-50 text-purple-600',
        teal: 'bg-teal-50 text-teal-600',
        slate: 'bg-slate-50 text-slate-600',
        sky: 'bg-sky-50 text-sky-600',
        rose: 'bg-rose-50 text-rose-600',
        amber: 'bg-amber-50 text-amber-600'
    };
   
    return (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-start space-x-4 hover:shadow-md transition-shadow">
            <div className={`p-3 rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
                <Icon className="w-6 h-6" />
            </div>
            <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{title}</p>
                <h3 className="text-xl font-bold text-slate-800">{value}</h3>
                {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
            </div>
        </div>
    );
};

const SectionHeader = ({ title, icon: Icon, action, color = "slate" }) => {
    const colorClasses = {
        blue: 'bg-blue-50 text-blue-600',
        orange: 'bg-orange-50 text-orange-600',
        pink: 'bg-pink-50 text-pink-600',
        emerald: 'bg-emerald-50 text-emerald-600',
        red: 'bg-red-50 text-red-600',
        green: 'bg-green-50 text-green-600',
        indigo: 'bg-indigo-50 text-indigo-700',
        purple: 'bg-purple-50 text-purple-600',
        teal: 'bg-teal-50 text-teal-600',
        slate: 'bg-slate-50 text-slate-600',
        sky: 'bg-sky-50 text-sky-600',
        rose: 'bg-rose-50 text-rose-600'
    };
   
    return (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-2 border-b border-slate-100">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.slate}`}>
                    <Icon className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
            </div>
            {action && <div className="mt-4 md:mt-0">{action}</div>}
        </div>
    );
};

// --- Weather Hub Component ---

const WeatherOperationsHub = ({ isSidebar = false, onToggle = null }) => {
    const [weatherData, setWeatherData] = useState({});
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchWeather = async () => {
        setLoading(true);
        const results = {};
        try {
            for (const loc of WEATHER_LOCATIONS) {
                const response = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current_weather=true&hourly=cloudcover`);
                const data = await response.json();
               
                const cloudCover = data.current_weather.cloudcover || 0;
                const windSpeed = data.current_weather.windspeed || 0; // km/h
               
                let flightRules = 'VFR';
                let statusColor = 'green';
               
                if (cloudCover > 80 || windSpeed > 40) {
                    flightRules = 'IFR';
                    statusColor = 'red';
                } else if (cloudCover > 50 || windSpeed > 25) {
                    flightRules = 'MVFR';
                    statusColor = 'amber';
                }

                results[loc.code] = {
                    temp: data.current_weather.temperature,
                    wind: windSpeed,
                    windDir: data.current_weather.winddirection,
                    clouds: cloudCover,
                    flightRules,
                    statusColor
                };
            }
            setWeatherData(results);
            setLastUpdated(new Date().toLocaleTimeString());
        } catch (err) {
            console.error("Weather fetch failed", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeather();
        const interval = setInterval(fetchWeather, 15 * 60 * 1000); // Auto-refresh every 15 mins
        return () => clearInterval(interval);
    }, []);

    if (isSidebar) {
        return (
            <div className="h-full flex flex-col bg-slate-900 text-white w-full border-l border-slate-800">
                <div className="p-4 border-b border-slate-800 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Cloud className="w-4 h-4 text-sky-400" />
                        <h3 className="font-bold text-xs uppercase tracking-widest">Pilot's WX Hub</h3>
                    </div>
                    <div className="flex items-center gap-2">
                         <button onClick={fetchWeather} disabled={loading} className="text-sky-400 hover:text-sky-300 disabled:opacity-50">
                            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        {onToggle && <button onClick={onToggle}><CloseIcon className="w-4 h-4 text-slate-500"/></button>}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {WEATHER_LOCATIONS.map(loc => {
                        const data = weatherData[loc.code];
                        const statusColors = {
                            green: 'bg-emerald-500',
                            amber: 'bg-amber-400',
                            red: 'bg-rose-500'
                        };
                        return (
                            <div key={loc.code} className="bg-slate-800 p-3 rounded-lg border border-slate-700 relative overflow-hidden">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h4 className="font-black text-xs leading-tight">{loc.name}</h4>
                                        <p className="text-[9px] font-bold text-slate-500 uppercase">{loc.code}</p>
                                    </div>
                                    {data && <div className={`px-1.5 py-0.5 rounded text-[9px] font-black ${statusColors[data.statusColor]}`}>{data.flightRules}</div>}
                                </div>
                                {data ? (
                                    <div className="grid grid-cols-2 gap-2 text-[10px] font-medium text-slate-300">
                                        <div className="flex items-center gap-1"><Thermometer className="w-2.5 h-2.5"/> {data.temp}°C</div>
                                        <div className="flex items-center gap-1"><Wind className="w-2.5 h-2.5"/> {data.wind}km</div>
                                    </div>
                                ) : <Loader2 className="w-4 h-4 animate-spin text-slate-700 mx-auto" />}
                                <div className={`absolute bottom-0 left-0 w-full h-1 ${data ? statusColors[data.statusColor] : 'bg-slate-700'}`}></div>
                            </div>
                        );
                    })}
                </div>
                {lastUpdated && <div className="p-2 bg-slate-950 text-[9px] text-center text-slate-600 font-mono">Updated: {lastUpdated}</div>}
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-900 border-b border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-sky-400" />
                    <h3 className="font-bold text-white text-sm uppercase tracking-widest">Pilot's Operations Weather</h3>
                </div>
                <div className="flex items-center gap-4">
                    {lastUpdated && <span className="text-[10px] text-slate-400 font-mono">Updated: {lastUpdated}</span>}
                    <button
                        onClick={fetchWeather}
                        disabled={loading}
                        className="text-sky-400 hover:text-sky-300 disabled:opacity-50 transition-colors"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>
           
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 bg-slate-50">
                {WEATHER_LOCATIONS.map(loc => {
                    const data = weatherData[loc.code];
                    const statusColors = {
                        green: 'bg-emerald-500 border-emerald-600 text-emerald-950',
                        amber: 'bg-amber-400 border-amber-500 text-amber-950',
                        red: 'bg-rose-500 border-rose-600 text-white'
                    };

                    return (
                        <div key={loc.code} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm relative group overflow-hidden">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h4 className="font-black text-slate-800 leading-tight">{loc.name}</h4>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{loc.code}</p>
                                </div>
                                {data && (
                                    <div className={`px-2 py-0.5 rounded text-[10px] font-black border ${statusColors[data.statusColor]}`}>
                                        {data.flightRules}
                                    </div>
                                )}
                            </div>

                            {data ? (
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <Thermometer className="w-3 h-3" />
                                            <span className="text-sm font-bold">{data.temp}°C</span>
                                        </div>
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <Wind className="w-3 h-3" />
                                            <span className="text-sm font-bold">{data.wind} km/h</span>
                                        </div>
                                    </div>
                                    <div className="text-[10px] text-slate-400 font-bold uppercase flex justify-between border-t border-slate-50 pt-2">
                                        <span>Clouds: {data.clouds}%</span>
                                        {loc.navCanada && (
                                            <a
                                                href={loc.navCanada}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sky-600 hover:underline flex items-center gap-1"
                                            >
                                                METAR <ExternalLink className="w-2 h-2"/>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="h-10 flex items-center justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                                </div>
                            )}
                           
                            <div className={`absolute bottom-0 left-0 w-full h-1 ${data ? (data.statusColor === 'green' ? 'bg-emerald-500' : data.statusColor === 'amber' ? 'bg-amber-400' : 'bg-rose-500') : 'bg-slate-100'}`}></div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- Main Application Component ---

export default function App() {
    // --- State ---
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);

    const [people, setPeople] = useState([]);
    const [guardianAngels, setGuardianAngels] = useState([]);
    const [serviceProviders, setServiceProviders] = useState([]);
    const [gear, setGear] = useState([]);
    const [groupFood, setGroupFood] = useState([]);
    const [canoes, setCanoes] = useState([]);
    const [repairKit, setRepairKit] = useState([]);
    const [dates, setDates] = useState({});
    const [topics, setTopics] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [personalLists, setPersonalLists] = useState({});
    const [personalMenus, setPersonalMenus] = useState({});
    const [menu, setMenu] = useState({});
    const [tripDuration, setTripDuration] = useState(DEFAULT_TRIP_DURATION);
    const [foodLbsPerDay, setFoodLbsPerDay] = useState(DEFAULT_FOOD_LBS_PER_DAY);
    const [logistics, setLogistics] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
   
    const [isLanded, setIsLanded] = useState(false);
    const [isOfflineMode, setIsOfflineMode] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const [isRestoring, setIsRestoring] = useState(false);
    const [restoreStatus, setRestoreStatus] = useState(null);
   
    // Expenses State
 const [isSettlementModalOpen, setIsSettlementModalOpen] = useState(false);
const [settlementData, setSettlementData] = useState({ from: '', to: '', amount: '' });
const [isSplitMode, setIsSplitMode] = useState(false);
const [expenseViewFilter, setExpenseViewFilter] = useState('all'); // 'all', 'paid', 'owed'
const [expandedExpense, setExpandedExpense] = useState(null);

    // Personal Portal State
    const [viewingAs, setViewingAs] = useState("");

    // UI State
    const [activeTab, setActiveTab] = useState('dashboard');
    const [personalMenuMode, setPersonalMenuMode] = useState('checklist');
    const [groupMenuMode, setGroupMenuMode] = useState('checklist');
   
    // New Logistics State
    const [logisticsPhase, setLogisticsPhase] = useState('staging'); // 'staging', 'approach', 'exit', 'homestretch', 'directory'
    const [isLogisticsEditing, setIsLogisticsEditing] = useState(false);
    const [isWeatherSidebarOpen, setIsWeatherSidebarOpen] = useState(false);
   
    // Group Menu State
    const [isGroupMenuEditing, setIsGroupMenuEditing] = useState(false);

    const [expandedSections, setExpandedSections] = useState({
        'travel_departure': true,
        'travel_return': true,
        'staging_basecamp': true,
        'bush_plane_depart': true,
        'bush_plane_return': true,
        'watershuttle_return': true,
        'logistics_providers': true,
        'weather_monitor': true
    });
   
    const [logisticsVisibility, setLogisticsVisibility] = useState({
        weather_monitor: true,
        departure_tracker: true,
        return_tracker: true,
        staging_basecamp: true,
        bush_plane_depart: true,
        bush_plane_return: true,
        watershuttle_return: true,
        providers: true
    });

    // Offline configuration state
    const [offlineOptions, setOfflineOptions] = useState({
        guardian: true,
        crew: true,
        dates: true,
        travel: true,
        loads: true,
        gear: true,
        group_food: false,
        menu: true,
        campfire: false,
        personal_gear: false,
        personal_menu: false,
        expenses: false
    });

    const offlineLabels = {
        guardian: "Guardian Angel",
        crew: "Crew Roster",
        dates: "Dates",
        travel: "Travel",
        loads: "Loads & Weights",
        gear: "Group Gear",
        group_food: "Group Food List",
        menu: "Group Menu Plan",
        campfire: "Tasks & Topics",
        personal_gear: "Personal Gear",
        personal_menu: "Personal Menu",
        expenses: "Expenses"
    };

    // Temporary inputs
    const [newItemName, setNewItemName] = useState('');
    const [addingToCategory, setAddingToCategory] = useState({ personId: null, category: null });
    const [newMenuItemName, setNewMenuItemName] = useState('');
    const [addingToMenuCategory, setAddingToMenuCategory] = useState({ personId: null, category: null });
    const [newGroupFoodName, setNewGroupFoodName] = useState('');
    const [addingToGroupFoodCategory, setAddingToGroupFoodCategory] = useState(null);
    const [newDateProposal, setNewDateProposal] = useState({ whoId: '', startDate: '', endDate: '', notes: '' });

    const landingImageUrl = "https://dl.dropboxusercontent.com/scl/fi/pofjab2awc6uqt8kmndh1/Mountainriver.jpg?rlkey=0rfly6uvh18a71zbhtnmizn1d&st=fbekkzj4";
    const confirmedPeople = useMemo(() => people.filter(p => p.confirmed), [people]);

    // --- Auth Logic ---
    useEffect(() => {
        const initAuth = async () => {
            try {
                if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
                    await signInWithCustomToken(auth, __initial_auth_token);
                }
            } catch (err) {
                console.error("Custom token auth failed:", err);
            }
        };
        initAuth();
        return onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (u) setIsLanded(true);
        });
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setIsLanded(true);
        } catch (err) {
            console.error("Login failed:", err);
            setAuthError("Invalid email or password. Please try again.");
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setAuthLoading(true);
        setAuthError(null);

        if (inviteCode !== SECRET_INVITE_CODE) {
            setAuthError("Invalid Invite Code. Please contact expedition leader Paul.");
            setAuthLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setAuthError("Passwords do not match.");
            setAuthLoading(false);
            return;
        }

        try {
            await createUserWithEmailAndPassword(auth, email, password);
            setIsLanded(true);
        } catch (err) {
            console.error("Sign up failed:", err);
            setAuthError(err.message || "Sign up failed. Please try again.");
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setIsLanded(false);
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setInviteCode('');
            setAuthMode('login');
        } catch (err) {
            console.error("Sign out failed:", err);
        }
    };

    // --- Data Listeners ---
    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }
       
        setLoading(true);
        const unsubs = [];
       
        const collectionMap = {
            'people': setPeople, 'guardian_angel': setGuardianAngels, 'service_providers': setServiceProviders,
            'gear': setGear, 'group_food_checklist': setGroupFood, 'canoes': setCanoes, 'repair_kit': setRepairKit,
            'expenses': setExpenses, 'campfire': setTopics, 'tasks': setTasks
        };

        Object.entries(collectionMap).forEach(([colName, setter]) => {
            unsubs.push(onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', colName), (snap) => {
                setter(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (a.createdAt || 0) - (b.createdAt || 0)));
                if(colName === 'gear') setLoading(false);
            }, (err) => console.error(`Error loading ${colName}:`, err)));
        });

        unsubs.push(onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'dates'), (snap) => {
            const dObj = {};
            snap.docs.forEach(d => { const dt = d.data(); if(!dObj[dt.whoId]) dObj[dt.whoId]=[]; dObj[dt.whoId].push({id: d.id, ...dt}); });
            Object.keys(dObj).forEach(k => dObj[k].sort((a,b)=>a.startDate.localeCompare(b.startDate)));
            setDates(dObj);
        }, (err) => console.error("Error loading dates:", err)));
       
        unsubs.push(onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'personal_lists_v2'), s => { const l={}; s.docs.forEach(d=>l[d.id]=d.data()); setPersonalLists(l); }, err => console.error(err)));
        unsubs.push(onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'personal_menus'), s => { const m={}; s.docs.forEach(d=>m[d.id]=d.data()); setPersonalMenus(m); }, err => console.error(err)));
        unsubs.push(onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'menu'), s => { const m={}; s.docs.forEach(d=>m[d.id]=d.data()); setMenu(m); }, err => console.error(err)));
        unsubs.push(onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'general'), d => { if(d.exists()) { setTripDuration(d.data().tripDuration || 15); setFoodLbsPerDay(d.data().foodLbsPerDay || 2.0); } }, err => console.error(err)));
        unsubs.push(onSnapshot(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'logistics'), d => { if(d.exists()) { setLogistics(d.data()); } }, err => console.error(err)));

        return () => unsubs.forEach(u => u());
    }, [user]);

    // --- CRUD Actions ---
    const crud = (col) => ({
        add: (data) => user && addDoc(collection(db, 'artifacts', appId, 'public', 'data', col), { ...data, createdAt: Date.now() }),
        update: (id, f, v) => user && updateDoc(doc(db, 'artifacts', appId, 'public', 'data', col, id), { [f]: v }),
        del: (id) => user && deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', col, id))
    });

    const addPerson = () => crud('people').add({ name: '', confirmed: false, email: '' });
    const updatePerson = (id, f, v) => crud('people').update(id, f, v);
    const removePerson = (id) => crud('people').del(id);
   
    const updateDocField = async (col, id, field, value) => { if(user) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', col, id), { [field]: value }); };
    const deleteDocument = async (col, id) => { if(user) await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', col, id)); };
   
    const addGear = () => crud('gear').add({ item: '', category: 'General', weight: 0, assignedTo: '', confirmed: false });
    const updateGear = (id, f, v) => updateDocField('gear', id, f, v);
    const removeGear = (id) => deleteDocument('gear', id);
   
    const addCanoe = () => crud('canoes').add({
        name: `Canoe ${canoes.length + 1}`,
        make: '',
        length: '16',
        weight: 0,
        hullWeight: 0,
        sprayskirtWeight: 0,
        paddleCount: 2,
        notes: '',
        bowPaddler: '',
        sternPaddler: '',
        spraydeck: false
    });
   
    const updateCanoe = (id, f, v) => updateDocField('canoes', id, f, v);
    const removeCanoe = (id) => deleteDocument('canoes', id);

    const addDateProposal = async () => {
        if (!user || !newDateProposal.whoId || !newDateProposal.startDate || !newDateProposal.endDate) return;
        const whoName = people.find(p => p.id === newDateProposal.whoId)?.name || 'Unknown';
        await crud('dates').add({ whoId: newDateProposal.whoId, who: whoName, startDate: newDateProposal.startDate, endDate: newDateProposal.endDate, notes: newDateProposal.notes });
        setNewDateProposal({ whoId: '', startDate: '', endDate: '', notes: '' });
    };
    const removeDate = (id) => deleteDocument('dates', id);

    const addGuardianAngel = () => crud('guardian_angel').add({ name: '', address: '', phone1: '', phone2: '', email1: '', email2: '' });
    const updateGuardianAngel = (id, f, v) => updateDocField('guardian_angel', id, f, v);
    const removeGuardianAngel = (id) => deleteDocument('guardian_angel', id);

    const addServiceProvider = () => crud('service_providers').add({ role: '', company: '', phone: '', notes: '' });
    const updateServiceProvider = (id, f, v) => updateDocField('service_providers', id, f, v);
    const removeServiceProvider = (id) => deleteDocument('service_providers', id);

    const addTopic = () => crud('campfire').add({ issue: '', raisedBy: '', conclusion: '' });
    const updateTopic = (id, f, v) => updateDocField('campfire', id, f, v);
    const removeTopic = (id) => deleteDocument('campfire', id);

    const addTask = () => crud('tasks').add({ role: '', assignedTo: '' });
    const updateTask = (id, f, v) => updateDocField('tasks', id, f, v);
    const removeTask = (id) => deleteDocument('tasks', id);

 const addExpense = () => crud('expenses').add({ 
    item: '', 
    totalCost: 0, 
    paidBy: {}, 
    splitWith: [], 
    notes: '',
    date: new Date().toISOString().split('T')[0],
    hasReceipt: false,
    paymentMethod: ''
});
    const updateExpense = (id, f, v) => updateDocField('expenses', id, f, v);
    const removeExpense = (id) => deleteDocument('expenses', id);
    const updateExpensePayment = async (eid, pid, amt) => { if(user) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', eid), { [`paidBy.${pid}`]: Number(amt) }); };
   
    const recordSettlement = async () => {
    if (!user || !settlementData.from || !settlementData.to || !settlementData.amount) return;
    const payerName = people.find(p => p.id === settlementData.from)?.name || 'Unknown';
    const payeeName = people.find(p => p.id === settlementData.to)?.name || 'Unknown';
    const amount = parseFloat(settlementData.amount);

    if (isNaN(amount) || amount <= 0) return;

    await crud('expenses').add({
        item: `Settlement: ${payerName} -> ${payeeName}`,
        totalCost: amount,
        paidBy: { [settlementData.from]: amount },
        splitWith: [settlementData.to],
        notes: 'Recorded Settlement',
        date: new Date().toISOString().split('T')[0],
        hasReceipt: false,
        paymentMethod: 'Settlement',
        isSettlement: true,
        settlementTimestamp: Date.now(),
        settlementCompleted: false
    });
    setSettlementData({ from: '', to: '', amount: '' });
    setIsSettlementModalOpen(false);
};

const exportExpensesToCSV = () => {
    const headers = ['Date', 'Item', 'Total Cost', 'Paid By', 'Split With', 'Payment Method', 'Receipt', 'Notes'];
    const rows = expenses.map(e => {
        const paidByNames = Object.entries(e.paidBy || {})
            .map(([pid, amt]) => `${getPersonName(pid)} ($${amt})`)
            .join('; ');
        const splitWithNames = (e.splitWith || []).map(pid => getPersonName(pid)).join('; ');
        
        return [
            e.date || '',
            e.item || '',
            e.totalCost || 0,
            paidByNames,
            splitWithNames || 'Everyone',
            e.paymentMethod || '',
            e.hasReceipt ? 'Yes' : 'No',
            e.notes || ''
        ];
    });
    
    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mountain-river-expenses-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const generateExpenseReport = () => {
    const reportWindow = window.open('', '_blank');
    const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Mountain River Expedition - Expense Report</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 40px; }
                h1 { color: #1e293b; border-bottom: 3px solid #10b981; padding-bottom: 10px; }
                h2 { color: #475569; margin-top: 30px; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th { background: #f1f5f9; padding: 12px; text-align: left; border: 1px solid #e2e8f0; font-weight: bold; }
                td { padding: 10px; border: 1px solid #e2e8f0; }
                tr:nth-child(even) { background: #f8fafc; }
                .summary { background: #ecfdf5; border: 2px solid #10b981; padding: 20px; margin: 20px 0; border-radius: 8px; }
                .balance-positive { color: #059669; font-weight: bold; }
                .balance-negative { color: #dc2626; font-weight: bold; }
                .footer { margin-top: 40px; text-align: center; color: #64748b; font-size: 12px; }
            </style>
        </head>
        <body>
            <h1>Mountain River Expedition - Expense Report</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            
            <div class="summary">
                <h2>Summary</h2>
                <p><strong>Total Trip Cost:</strong> $${safeFixed(expenseSummary?.totalTripCost || 0, 2)}</p>
                <p><strong>Number of Expenses:</strong> ${expenses.length}</p>
                <p><strong>Number of Crew Members:</strong> ${confirmedPeople.length}</p>
            </div>
            
            <h2>Individual Balances</h2>
            <table>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Total Paid</th>
                        <th>Share of Costs</th>
                        <th>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    ${expenseSummary?.balances.map(b => `
                        <tr>
                            <td>${b.name}</td>
                            <td>$${safeFixed(b.paid, 2)}</td>
                            <td>$${safeFixed(b.share, 2)}</td>
                            <td class="${b.balance >= 0 ? 'balance-positive' : 'balance-negative'}">
                                ${b.balance >= 0 ? '+' : ''}$${safeFixed(b.balance, 2)}
                            </td>
                        </tr>
                    `).join('')}
                </tbody>
            </table>
            
            ${settlements.length > 0 ? `
                <h2>Recommended Settlements</h2>
                <table>
                    <thead>
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${settlements.map(s => `
                            <tr>
                                <td>${s.from}</td>
                                <td>${s.to}</td>
                                <td>$${safeFixed(s.amount, 2)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            ` : '<p style="color: #059669; font-weight: bold;">✓ All settled up! No outstanding balances.</p>'}
            
            <h2>Detailed Expenses</h2>
            <table>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Item</th>
                        <th>Cost</th>
                        <th>Paid By</th>
                        <th>Payment Method</th>
                        <th>Receipt</th>
                        <th>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    ${expenses.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(e => {
                        const paidByNames = Object.entries(e.paidBy || {})
                            .map(([pid, amt]) => `${getPersonName(pid)} ($${amt})`)
                            .join(', ');
                        return `
                            <tr>
                                <td>${e.date || '-'}</td>
                                <td>${e.item || ''}</td>
                                <td>$${safeFixed(e.totalCost, 2)}</td>
                                <td>${paidByNames || '-'}</td>
                                <td>${e.paymentMethod || '-'}</td>
                                <td>${e.hasReceipt ? '✓' : '-'}</td>
                                <td>${e.notes || ''}</td>
                            </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
            
            <div class="footer">
                <p>Mountain River Expedition 2026 - Expense Report</p>
                <p>This report was generated automatically. Please verify all amounts.</p>
            </div>
        </body>
        </html>
    `;
    
    reportWindow.document.write(reportHTML);
    reportWindow.document.close();
    reportWindow.print();
};

const copyExpenseSummaryToClipboard = () => {
    const summary = `Mountain River Expedition - Expense Summary
Generated: ${new Date().toLocaleString()}

TOTAL TRIP COST: $${safeFixed(expenseSummary?.totalTripCost || 0, 2)}

INDIVIDUAL BALANCES:
${expenseSummary?.balances.map(b => 
    `${b.name}: ${b.balance >= 0 ? '+' : ''}$${safeFixed(b.balance, 2)} (Paid: $${safeFixed(b.paid, 2)}, Share: $${safeFixed(b.share, 2)})`
).join('\n')}

${settlements.length > 0 ? `
RECOMMENDED SETTLEMENTS:
${settlements.map(s => `${s.from} → ${s.to}: $${safeFixed(s.amount, 2)}`).join('\n')}
` : 'All settled up! No outstanding balances.'}`;

    navigator.clipboard.writeText(summary).then(() => {
        alert('Expense summary copied to clipboard! You can paste this into an email.');
    });
};

const undoSettlement = async (expenseId) => {
    if (!user) return;
    if (window.confirm('Are you sure you want to undo this settlement?')) {
        await deleteDocument('expenses', expenseId);
    }
};




    const toggleExpenseSplit = async (exp, pid) => {
        if (!user) return;
        let currentSplit = exp.splitWith || [];
        if (currentSplit.length === 0) {
            const allIds = confirmedPeople.map(p => p.id);
            const newSplit = allIds.filter(id => id !== pid);
            await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', exp.id), { splitWith: newSplit });
        } else {
            if (currentSplit.includes(pid)) await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', exp.id), { splitWith: arrayRemove(pid) });
            else await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', exp.id), { splitWith: arrayUnion(pid) });
        }
    };

    const handleExpenseCostUpdate = async (expense, newCostStr) => {
        const newCost = parseFloat(newCostStr) || 0;
        await updateExpense(expense.id, 'totalCost', newCost);
        if (!isSplitMode) {
            const currentPayers = Object.keys(expense.paidBy || {});
            if (currentPayers.length === 1) {
                await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'expenses', expense.id), { [`paidBy.${currentPayers[0]}`]: newCost });
            }
        }
    };

    const addGroupFoodItem = (category) => {
        if (!user || !newGroupFoodName.trim()) return;
        crud('group_food_checklist').add({ item: newGroupFoodName.trim(), category: category || 'General', weight: 0, packed: false });
        setNewGroupFoodName('');
        setAddingToGroupFoodCategory(null);
    };
    const updateGroupFoodItem = (id, f, v) => updateDocField('group_food_checklist', id, f, v);
    const removeGroupFoodItem = (id) => deleteDocument('group_food_checklist', id);
    const addStandardGroupFood = async (category) => {
        if (!user) return;
        const batch = writeBatch(db);
        (standardGroupFood[category] || []).forEach(item => {
            const ref = doc(collection(db, 'artifacts', appId, 'public', 'data', 'group_food_checklist'));
            batch.set(ref, { item, category, weight: 0, packed: false, createdAt: Date.now() });
        });
        await batch.commit();
    };

    const generatePersonalList = async (personId) => {
        if (!user) return;
        const initialItems = [];
        Object.entries(standardPersonalKit).forEach(([category, items]) =>
            items.forEach(item => initialItems.push({ id: crypto.randomUUID(), category, name: item, weight: 0, packed: false }))
        );
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_lists_v2', personId), { items: initialItems });
    };
    const updatePersonalItem = async (pid, iid, f, v) => {
        if (!user || !personalLists[pid]) return;
        const updatedItems = personalLists[pid].items.map(i => i.id === iid ? { ...i, [f]: v } : i);
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_lists_v2', pid), { items: updatedItems });
    };
    const removePersonalItem = async (pid, iid) => {
        if (!user || !personalLists[pid]) return;
        const updatedItems = personalLists[pid].items.filter(i => i.id !== iid);
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_lists_v2', pid), { items: updatedItems });
    };
    const addCustomItem = async () => {
        if (!user || !newItemName.trim() || !addingToCategory.personId) return;
        const { personId, category } = addingToCategory;
        const newItem = { id: crypto.randomUUID(), category, name: newItemName.trim(), weight: 0, packed: false };
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_lists_v2', personId), { items: [...(personalLists[personId]?.items || []), newItem] });
        setNewItemName(''); setAddingToCategory({ personId: null, category: null });
    };

    const generatePersonalMenu = async (personId) => {
        if (!user) return;
        const initialItems = [];
        Object.entries(standardPersonalMenu).forEach(([category, items]) =>
            items.forEach(item => initialItems.push({ id: crypto.randomUUID(), category, name: item, weight: 0, packed: false }))
        );
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId), { items: initialItems }, { merge: true });
    };
    const updatePersonalMenuItem = async (personId, itemId, field, value) => {
        if (!user || !personalMenus[personId]) return;
        const updatedItems = personalMenus[personId].items.map(item => item.id === itemId ? { ...item, [field]: value } : item);
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId), { items: updatedItems });
    };
    const removePersonalMenuItem = async (personId, itemId) => {
        if (!user || !personalMenus[personId]) return;
        const updatedItems = personalMenus[personId].items.filter(item => item.id !== itemId);
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId), { items: updatedItems });
    };
    const addCustomPersonalMenuItem = async () => {
        if (!user || !newMenuItemName.trim() || !addingToMenuCategory.personId) return;
        const { personId, category } = addingToMenuCategory;
        const newItem = { id: crypto.randomUUID(), category, name: newMenuItemName.trim(), weight: 0, packed: false };
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId), { items: [...(personalMenus[personId]?.items || []), newItem] });
        setNewMenuItemName(''); setAddingToMenuCategory({ personId: null, category: null });
    };
    const updatePersonalDailyMenu = async (personId, day, meal, value) => {
        if (!user) return;
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId), { daily: { [`day_${day}`]: { [meal]: value } } }, { merge: true });
    };
    const addPersonalDailyItem = async (personId, day, meal, itemId) => {
        if (!user || !itemId) return;
        const fieldName = `${meal}_items`;
        const currentRef = doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId);
        const updateField = `daily.day_${day}.${fieldName}`;
        await updateDoc(currentRef, { [updateField]: arrayUnion(itemId) }).catch(async (e) => {
            await setDoc(currentRef, { daily: { [`day_${day}`]: { [fieldName]: [itemId] } } }, { merge: true });
        });
    };
    const removePersonalDailyItem = async (personId, day, meal, itemId) => {
        if (!user) return;
        const fieldName = `${meal}_items`;
        const currentRef = doc(db, 'artifacts', appId, 'public', 'data', 'personal_menus', personId);
        const updateField = `daily.day_${day}.${fieldName}`;
        await updateDoc(currentRef, { [updateField]: arrayRemove(itemId) });
    };

    const updateMenu = async (day, mealType, field, value) => {
        if (!user) return;
        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'menu', `day_${day}`), { [`${mealType}_${field}`]: value }, { merge: true });
    };
    const addGroupDailyItem = async (day, meal, itemId) => {
        if (!user || !itemId) return;
        const fieldName = `${meal}_items`;
        const currentRef = doc(db, 'artifacts', appId, 'public', 'data', 'menu', `day_${day}`);
        try { await updateDoc(currentRef, { [fieldName]: arrayUnion(itemId) }); }
        catch (e) { await setDoc(currentRef, { [fieldName]: [itemId] }, { merge: true }); }
    };
    const removeGroupDailyItem = async (day, meal, itemId) => {
        if (!user) return;
        const fieldName = `${meal}_items`;
        const currentRef = doc(db, 'artifacts', appId, 'public', 'data', 'menu', `day_${day}`);
        await updateDoc(currentRef, { [fieldName]: arrayRemove(itemId) });
    };

    const addRepairItem = () => crud('repair_kit').add({ item: '', assignedTo: '', weight: 0, confirmed: false });
    const updateRepairItem = (id, f, v) => updateDocField('repair_kit', id, f, v);
    const removeRepairItem = (id) => deleteDocument('repair_kit', id);

    const updateDuration = async (days) => { if(user) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'general'), { tripDuration: Number(days) }, { merge: true }); };
   
    // Global Logistics Update
    const updateLogistics = async (field, value) => { if(user) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'logistics'), { [field]: value }, { merge: true }); };

    // Push Logistics to Expense
    const pushToExpense = async (item, amount, notes) => {
        if(!user || !amount || amount <= 0) return;
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'expenses'), {
            item, totalCost: Number(amount), notes, paidBy: {}, splitWith: [], createdAt: Date.now()
        });
    };

    // --- Backup & Restore Logic ---
    const downloadBackup = async () => {
        try {
            const backupData = {
                version: '2.1',
                timestamp: new Date().toISOString(),
                tripDuration,
                foodLbsPerDay,
                logistics,
                people,
                guardianAngels,
                serviceProviders,
                gear,
                groupFood,
                canoes,
                repairKit,
                expenses,
                campfire: topics,
                tasks,
                personalLists,
                personalMenus,
                menu,
                dates
            };

            const json = JSON.stringify(backupData, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `mountain-river-backup-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Backup failed", err);
        }
    };

    const restoreFromBackup = async (event) => {
        const file = event.target.files[0];
        if (!file || !user) return;

        setIsRestoring(true);
        setRestoreStatus("Reading file...");

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = JSON.parse(e.target.result);
                setRestoreStatus("Writing data to Firebase...");

                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'general'), {
                    tripDuration: data.tripDuration || 15,
                    foodLbsPerDay: data.foodLbsPerDay || 2.0
                }, { merge: true });

                if(data.logistics) await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'settings', 'logistics'), data.logistics, { merge: true });

                const restoreCollection = async (items, colName) => {
                    if (!items || !Array.isArray(items)) return;
                    for (const item of items) {
                        const { id, ...payload } = item;
                        if (id) {
                            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', colName, id), payload, { merge: true });
                        }
                    }
                };

                const restoreKeyedCollection = async (dataMap, colName) => {
                    if (!dataMap) return;
                    for (const [id, payload] of Object.entries(dataMap)) {
                        await setDoc(doc(db, 'artifacts', appId, 'public', 'data', colName, id), payload, { merge: true });
                    }
                };

                await restoreCollection(data.people, 'people');
                await restoreCollection(data.guardianAngels, 'guardian_angel');
                await restoreCollection(data.serviceProviders, 'service_providers');
                await restoreCollection(data.gear, 'gear');
                await restoreCollection(data.groupFood, 'group_food_checklist');
                await restoreCollection(data.canoes, 'canoes');
                await restoreCollection(data.repairKit, 'repair_kit');
                await restoreCollection(data.expenses, 'expenses');
                await restoreCollection(data.campfire, 'campfire');
                await restoreCollection(data.tasks, 'tasks');
               
                await restoreKeyedCollection(data.personalLists, 'personal_lists_v2');
                await restoreKeyedCollection(data.personalMenus, 'personal_menus');
                await restoreKeyedCollection(data.menu, 'menu');

                if (data.dates) {
                    for (const proposals of Object.values(data.dates)) {
                        await restoreCollection(proposals, 'dates');
                    }
                }

                setRestoreStatus("Success! Your data has been restored.");
                setTimeout(() => { setIsRestoring(false); setRestoreStatus(null); }, 3000);
            } catch (err) {
                console.error("Restore failed", err);
                setRestoreStatus("Error: Invalid backup file format.");
                setTimeout(() => { setIsRestoring(false); }, 4000);
            }
        };
        reader.readAsText(file);
    };

    // --- Calculations & Memos ---
    const calculateDays = (s, e) => { if(!s||!e) return 0; const diff = new Date(e) - new Date(s); return diff < 0 ? 0 : Math.ceil(diff/(86400000))+1; };
    const calculatePersonalWeight = (pid) => personalLists[pid]?.items?.reduce((a,i) => a + Number(i.weight||0), 0) || 0;
    const calculatePersonalMenuChecklistWeight = (pid) => personalMenus[pid]?.items?.reduce((a,i) => a + Number(i.weight||0), 0) || 0;
   
    const safeCalculate = (fn, fallback = 0) => {
        try {
            const result = fn();
            return (typeof result === 'number' && !isNaN(result)) ? result : fallback;
        } catch (e) {
            console.error('Calculation error:', e);
            return fallback;
        }
    };

    const calculateGroupFoodWeight = () => {
        if (!groupFood || groupFood.length === 0) return 0;
        return groupFood.reduce((a,i) => a + Number(i.weight||0), 0);
    };

    const calculatePackedGroupFoodWeight = () => {
        if (!groupFood || groupFood.length === 0) return 0;
        return groupFood.filter(i => i.packed).reduce((a,i) => a + Number(i.weight||0), 0);
    };

    const calculateTotalPlanWeight = () => {
        if (!menu || !groupFood) return 0;
        const usedItemIds = new Set();
        for (let i = 1; i <= tripDuration; i++) {
            const dayKey = `day_${i}`;
            const dayData = menu[dayKey];
            if (!dayData) continue;
            ['breakfast', 'lunch', 'dinner', 'dessert', 'staples'].forEach(meal => {
                const items = dayData[`${meal}_items`] || [];
                items.forEach(id => usedItemIds.add(id));
            });
        }
       
        let total = 0;
        usedItemIds.forEach(id => {
            const item = groupFood.find(i => i.id === id);
            if (item) total += Number(item.weight || 0);
        });
        return total;
    };

    const calculatePersonalMenuPlanWeight = (personId) => {
        const menuData = personalMenus[personId];
        if (!menuData || !menuData.items || !menuData.daily) return 0;
       
        const itemMap = new Map(menuData.items.map(i => [i.id, Number(i.weight || 0)]));
        let total = 0;
       
        Object.values(menuData.daily).forEach(day => {
            ['breakfast', 'lunch', 'dinner', 'snacks', 'drinks', 'desserts'].forEach(meal => {
                const itemIds = day[`${meal}_items`] || [];
                itemIds.forEach(id => {
                    total += (itemMap.get(id) || 0);
                });
            });
        });
        return total;
    };

    const totalHullWeight = safeCalculate(() =>
        (canoes || []).reduce((acc, c) => acc + Number(c.weight || c.hullWeight || 0), 0)
    );

    const totalSprayskirtWeight = safeCalculate(() =>
        (canoes || []).reduce((acc, c) => acc + (c.spraydeck ? Number(c.sprayskirtWeight || 0) : 0), 0)
    );

    const totalCanoeWeight = totalHullWeight + totalSprayskirtWeight;

    const totalGroupGearWeight = safeCalculate(() =>
        (gear || []).reduce((a,i) => a + Number(i.weight||0), 0) +
        (repairKit || []).reduce((a,i) => a+Number(i.weight||0), 0) +
        calculateGroupFoodWeight()
    );

    const totalGearManifestWeight = safeCalculate(() =>
        (gear || []).reduce((a,i) => a + Number(i.weight||0), 0)
    );

    const estimatedTotalFoodWeight = safeCalculate(() => people.length * tripDuration * foodLbsPerDay);
    const totalPersonalGearWeight = safeCalculate(() => people.reduce((a,p) => a + calculatePersonalWeight(p.id), 0));
    const totalPersonalMenuWeight = safeCalculate(() => people.reduce((a,p) => a + calculatePersonalMenuChecklistWeight(p.id), 0));
    const avgPersonalGearWeight = safeCalculate(() => people.length > 0 ? (totalPersonalGearWeight / people.length) : 0);

    const totalExpeditionWeight = safeCalculate(() => totalGroupGearWeight + totalPersonalGearWeight + totalPersonalMenuWeight + totalCanoeWeight + estimatedTotalFoodWeight);

    const packingStats = useMemo(() => {
        let totalPersonalItems = 0, packedPersonalItems = 0;
        Object.values(personalLists).forEach(list => {
            const items = list.items || [];
            totalPersonalItems += items.length;
            packedPersonalItems += items.filter(i => i.packed).length;
        });
        const personalGearPercent = totalPersonalItems > 0 ? (packedPersonalItems / totalPersonalItems) * 100 : 0;

        let totalPersonalMenuItems = 0, packedPersonalMenuItems = 0;
        Object.values(personalMenus).forEach(list => {
            const items = list.items || [];
            totalPersonalMenuItems += items.length;
            packedPersonalMenuItems += items.filter(i => i.packed).length;
        });
        const personalMenuPercent = totalPersonalMenuItems > 0 ? (packedPersonalMenuItems / totalPersonalMenuItems) * 100 : 0;

        const totalGroupFood = groupFood.length;
        const packedGroupFood = groupFood.filter(i => i.packed).length;
        const groupFoodPercent = totalGroupFood > 0 ? (packedGroupFood / totalGroupFood) * 100 : 0;

        return {
            personalGear: { total: totalPersonalItems, packed: packedPersonalItems, percent: personalGearPercent },
            personalMenu: { total: totalPersonalMenuItems, packed: packedPersonalMenuItems, percent: personalMenuPercent },
            groupFood: { total: totalGroupFood, packed: packedGroupFood, percent: groupFoodPercent }
        };
    }, [personalLists, personalMenus, groupFood]);

    const shoppingAggregates = useMemo(() => {
        const itemUsage = {}, mealFrequency = {};
        for (let i = 1; i <= tripDuration; i++) {
            const dayKey = `day_${i}`, dayData = menu[dayKey];
            if (!dayData) continue;
            ['breakfast', 'lunch', 'dinner', 'dessert', 'staples'].forEach(meal => {
                const itemIds = dayData[`${meal}_items`] || [];
                itemIds.forEach(id => { itemUsage[id] = (itemUsage[id] || 0) + 1; });
                const text = dayData[`${meal}_meal`];
                if (text && text.trim()) {
                    const normalized = text.toLowerCase().trim();
                    if (!mealFrequency[normalized]) mealFrequency[normalized] = { name: text.trim(), count: 0, examples: [] };
                    mealFrequency[normalized].count += 1;
                    if (mealFrequency[normalized].examples.length < 3) mealFrequency[normalized].examples.push(`Day ${i}`);
                }
            });
        }
        return { itemUsage, mealFrequency };
    }, [menu, tripDuration]);

    const dateConsensus = useMemo(() => {
        const allProposals = Object.values(dates).flat();
        if (allProposals.length === 0) return [];
        const allDates = new Set();
        allProposals.forEach(d => {
            const start = new Date(d.startDate), end = new Date(d.endDate);
            for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
                allDates.add(dt.toISOString().substring(0, 10));
            }
        });
        const sortedDates = Array.from(allDates).sort();
        const availabilityMap = {};
        sortedDates.forEach(dateStr => {
            let availableCount = 0;
            allProposals.forEach(d => { if (dateStr >= d.startDate && dateStr <= d.endDate) availableCount++; });
            availabilityMap[dateStr] = availableCount;
        });
        const segments = [];
        let currentSegment = null;
        sortedDates.forEach(dateStr => {
            const count = availabilityMap[dateStr];
            const date = new Date(dateStr), yesterday = new Date(date);
            yesterday.setDate(yesterday.getDate() - 1);
            const previousCount = availabilityMap[yesterday.toISOString().substring(0, 10)];
            if (previousCount === undefined || count !== previousCount) {
                if (currentSegment) segments.push(currentSegment);
                currentSegment = { start: date, end: date, count: count };
            } else if (currentSegment) {
                currentSegment.end = date;
            }
        });
        if (currentSegment) segments.push(currentSegment);
        return segments.filter(s => s.count > 0).sort((a, b) => b.count - a.count || a.start - b.start).map(s => ({
            ...s,
            startStr: s.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            endStr: s.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            duration: calculateDays(s.start.toISOString().substring(0, 10), s.end.toISOString().substring(0, 10))
        }));
    }, [dates]);

    const expenseSummary = useMemo(() => {
        if (confirmedPeople.length === 0) return null;
        let totalTripCost = 0;
        const paidByPerson = {}, shareOfCosts = {};
        confirmedPeople.forEach(p => { paidByPerson[p.id] = 0; shareOfCosts[p.id] = 0; });
        expenses.forEach(e => {
            const cost = parseFloat(e.totalCost) || 0;
            totalTripCost += cost;
            Object.entries(e.paidBy || {}).forEach(([pid, amount]) => { if (paidByPerson[pid] !== undefined) paidByPerson[pid] += (parseFloat(amount) || 0); });
            let involvedIDs = e.splitWith || [];
            involvedIDs = involvedIDs.filter(id => paidByPerson[id] !== undefined);
            if (involvedIDs.length === 0) involvedIDs = confirmedPeople.map(p => p.id);
            if (involvedIDs.length > 0) {
                const costPerPerson = cost / involvedIDs.length;
                involvedIDs.forEach(pid => { if (shareOfCosts[pid] !== undefined) shareOfCosts[pid] += costPerPerson; });
            }
        });
        const balances = confirmedPeople.map(p => ({
            id: p.id, name: p.name, paid: paidByPerson[p.id] || 0, share: shareOfCosts[p.id] || 0, balance: (paidByPerson[p.id] || 0) - (shareOfCosts[p.id] || 0)
        }));
        return { totalTripCost, balances };
    }, [expenses, confirmedPeople]);

    const settlements = useMemo(() => {
        if (!expenseSummary) return [];
        let debtors = expenseSummary.balances.filter(b => b.balance < -0.01).map(b => ({...b})).sort((a,b) => a.balance - b.balance);
        let creditors = expenseSummary.balances.filter(b => b.balance > 0.01).map(b => ({...b})).sort((a,b) => b.balance - a.balance);
        const transactions = [];
        let i = 0, j = 0;
        while (i < debtors.length && j < creditors.length) {
            const debtor = debtors[i], creditor = creditors[j];
            const amount = Math.min(Math.abs(debtor.balance), creditor.balance);
            const roundedAmount = Math.round(amount * 100) / 100;
            if (roundedAmount > 0) transactions.push({ from: debtor.name, to: creditor.name, amount: roundedAmount });
            debtor.balance += amount; creditor.balance -= amount;
            if (Math.abs(debtor.balance) < 0.01) i++;
            if (creditor.balance < 0.01) j++;
        }
        return transactions;
    }, [expenseSummary]);

    // --- Helpers ---
    const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
    const isSectionExpanded = (id) => !!expandedSections[id];
    const getPersonName = (id) => people.find(p => p.id === id)?.name || 'Unassigned';

    // --- Navigation Config ---
    const tabs = [
        { id: 'dashboard', icon: LayoutList, label: 'Dashboard', color: 'indigo' },
        { id: 'team', icon: Users, label: 'The Crew', color: 'blue' },
        { id: 'campfire', icon: Flame, label: 'Campfire', color: 'red' },
        { id: 'dates', icon: Calendar, label: 'Trip Dates', color: 'purple' },
        { id: 'travel', icon: Plane, label: 'Logistics', color: 'sky' },
        { id: 'gear', icon: Anchor, label: 'Fleet & Gear', color: 'orange' },
        { id: 'group_menu', icon: Utensils, label: 'Group Menu', color: 'pink' },
        { id: 'personal_menu', icon: Coffee, label: 'Personal Menu', color: 'teal' },
        { id: 'personal', icon: Backpack, label: 'Personal Gear', color: 'green' },
        { id: 'load_weights', icon: Scale, label: 'Loads & Weights', color: 'rose' },
        { id: 'expenses', icon: DollarSign, label: 'Expenses', color: 'emerald' },
        { id: 'settings', icon: Settings, label: 'Data & Settings', color: 'slate' },
    ];

    const NavItem = ({ tab, isActive, onClick }) => {
        const activeClasses = {
            indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
            blue: 'bg-blue-50 text-blue-700 border-blue-100',
            purple: 'bg-purple-50 text-purple-700 border-purple-100',
            sky: 'bg-sky-50 text-sky-700 border-sky-100',
            orange: 'bg-orange-50 text-orange-700 border-orange-100',
            pink: 'bg-pink-50 text-pink-700 border-pink-100',
            teal: 'bg-teal-50 text-teal-700 border-teal-100',
            green: 'bg-green-50 text-green-700 border-green-100',
            rose: 'bg-rose-50 text-rose-700 border-rose-100',
            red: 'bg-red-50 text-red-700 border-red-100',
            emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
            slate: 'bg-slate-50 text-slate-700 border-slate-100'
        };
       
        const iconActiveClasses = {
            indigo: 'text-indigo-600',
            blue: 'text-blue-600',
            purple: 'text-purple-600',
            sky: 'text-sky-600',
            orange: 'text-orange-600',
            pink: 'text-pink-600',
            teal: 'text-teal-600',
            green: 'text-green-600',
            rose: 'text-rose-600',
            red: 'text-red-600',
            emerald: 'text-emerald-600',
            slate: 'text-slate-600'
        };
       
        const dotClasses = {
            indigo: 'bg-indigo-500',
            blue: 'bg-blue-500',
            purple: 'bg-purple-500',
            sky: 'bg-sky-500',
            orange: 'bg-orange-500',
            pink: 'bg-pink-500',
            teal: 'bg-teal-500',
            green: 'bg-green-500',
            rose: 'bg-rose-500',
            red: 'bg-red-500',
            emerald: 'bg-emerald-500',
            slate: 'bg-slate-500'
        };
       
        return (
            <button
                onClick={onClick}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200
                    ${isActive
                        ? `${activeClasses[tab.color] || activeClasses.slate} shadow-sm border`
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
            >
                <tab.icon className={`w-5 h-5 ${isActive ? (iconActiveClasses[tab.color] || 'text-slate-600') : 'text-slate-400'}`} />
                <span>{tab.label}</span>
                {isActive && <div className={`ml-auto w-1.5 h-1.5 rounded-full ${dotClasses[tab.color] || 'bg-slate-500'}`} />}
            </button>
        );
    };

    // --- Logistics Sub-Components ---
    const TrafficLight = ({ label, status, subtext, icon: Icon }) => {
        const colors = {
            green: 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-500',
            yellow: 'bg-amber-50 text-amber-700 border-amber-200 ring-amber-500',
            red: 'bg-rose-50 text-rose-700 border-rose-200 ring-rose-500'
        };
        const dots = {
            green: 'bg-emerald-500',
            yellow: 'bg-amber-500',
            red: 'bg-rose-500'
        };
        return (
            <div className={`p-3 rounded-xl border flex items-center gap-3 transition-all ${colors[status]}`}>
                <div className={`p-2 rounded-lg bg-white/50`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{label}</span>
                        <div className={`w-2 h-2 rounded-full shadow-[0_0_8px] ${dots[status]} ring-2 ring-white`}></div>
                    </div>
                    <div className="text-sm font-bold truncate">{subtext}</div>
                </div>
            </div>
        );
    };

    const EditableLogisticsField = ({ label, value, field, type="text", placeholder, icon: Icon }) => {
        if (!isLogisticsEditing) {
            return (
                <div className="group transition-all">
                    <label className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">{label}</label>
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
                        <span className={!value ? 'text-slate-300 italic' : ''}>{value || 'Not set'}</span>
                    </div>
                </div>
            );
        }
        return (
            <div>
                <label className="text-[10px] font-bold text-indigo-500 uppercase block mb-1">{label}</label>
                <div className="flex items-center gap-2 border-b border-slate-200 focus-within:border-indigo-500 pb-1 transition-colors">
                    {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
                    <input
                        type={type}
                        value={value || ''}
                        onChange={e => updateLogistics(field, e.target.value)}
                        className="w-full text-sm outline-none bg-transparent"
                        placeholder={placeholder}
                    />
                </div>
            </div>
        );
    };

    // --- Auth & Landing Rendering ---
    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cover bg-center relative overflow-hidden" style={{ backgroundImage: `url(${landingImageUrl})` }}>
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/80"></div>
               
                <div className="relative z-10 m-4 p-8 max-w-md w-full bg-slate-950/10 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl transition-all duration-500">
                    <div className="mb-6 flex justify-center">
                        <div className="p-4 bg-white/10 rounded-full border border-white/20 shadow-inner">
                            <MapIcon className="w-12 h-12 text-white drop-shadow-md" />
                        </div>
                    </div>
                   
                    <h1 className="text-4xl font-black tracking-tight text-white mb-2 drop-shadow-md text-center uppercase">Mountain River</h1>
                    <p className="text-lg text-blue-100 font-medium mb-8 drop-shadow text-center">Expedition Planner • Summer 2026</p>
                   
                    {authMode === 'login' ? (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Crew Email</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 w-4 h-4 text-white/50" />
                                    <input
                                        type="email"
                                        required
                                        className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        placeholder="your@email.com"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 w-4 h-4 text-white/50" />
                                    <input
                                        type="password"
                                        required
                                        className="w-full bg-white/10 border border-white/20 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            {authError && (
                                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-xs flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    <span>{authError}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={authLoading}
                                className="group w-full bg-white text-slate-900 hover:bg-blue-50 py-4 rounded-xl font-black text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 transform active:scale-95 disabled:opacity-50"
                            >
                                {authLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><span>Sign Into Basecamp</span><ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
                            </button>
                           
                            <div className="text-center mt-4">
                                <button type="button" onClick={() => { setAuthMode('signup'); setAuthError(null); }} className="text-white/60 hover:text-white text-sm font-medium transition-colors">
                                    New crew member? <span className="underline decoration-blue-400 underline-offset-4">Sign Up here</span>
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleSignUp} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Shared Invite Code</label>
                                <div className="relative">
                                    <KeyRound className="absolute left-3 top-3 w-4 h-4 text-white/50" />
                                    <input
                                        type="text"
                                        required
                                        className="w-full bg-white/20 border-2 border-blue-400/50 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all font-mono tracking-widest"
                                        placeholder="Enter Secret Code"
                                        value={inviteCode}
                                        onChange={e => setInviteCode(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Password</label>
                                    <input
                                        type="password"
                                        required
                                        className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 text-white focus:outline-none"
                                        placeholder="••••••"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-white/70 uppercase tracking-widest mb-1 ml-1">Confirm</label>
                                    <input
                                        type="password"
                                        required
                                        className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 text-white focus:outline-none"
                                        placeholder="••••••"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                    />
                                </div>
                            </div>

                            {authError && (
                                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-xs flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                    <span>{authError}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={authLoading}
                                className="w-full bg-blue-600 text-white hover:bg-blue-500 py-4 rounded-xl font-black text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
                            >
                                {authLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : "Create Crew Account"}
                            </button>

                            <div className="text-center mt-4">
                                <button type="button" onClick={() => { setAuthMode('login'); setAuthError(null); }} className="text-white/60 hover:text-white text-sm font-medium transition-colors">
                                    Already have an account? <span className="underline decoration-blue-400 underline-offset-4">Log In</span>
                                </button>
                            </div>
                        </form>
                    )}
                   
                    <p className="mt-8 text-[10px] text-white/40 font-bold uppercase tracking-widest text-center">
                        Authorized Personnel Only • IP LOGGED
                    </p>
                </div>
            </div>
        );
    }

    if (loading) return <div className="h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
   
    if (isOfflineMode) {
        return (
            <div className="min-h-screen bg-white text-black p-8 font-serif">
                <div className="print:hidden mb-8 border-b-2 border-black pb-6">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h1 className="text-2xl font-bold uppercase tracking-widest mb-2 flex items-center gap-3">
                                <Clipboard className="w-8 h-8" /> Field Clipboard Mode
                            </h1>
                            <p className="text-sm text-slate-600 max-w-xl">
                                This layout is optimized for printing on waterproof paper or saving as a high-contrast PDF for field use.
                                Select the sections you want to include below.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={() => window.print()}
                                className="bg-black text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-slate-800 flex items-center gap-2"
                            >
                                <Printer className="w-5 h-5" /> Print / Save PDF
                            </button>
                            <button
                                onClick={() => setIsOfflineMode(false)}
                                className="border-2 border-black px-6 py-3 font-bold uppercase tracking-wider hover:bg-slate-100"
                            >
                                Exit Field Mode
                            </button>
                        </div>
                    </div>

                    <div className="bg-slate-100 p-4 border border-slate-300">
                        <h3 className="font-bold uppercase text-xs mb-3">Select Sections to Include:</h3>
                        <div className="flex flex-wrap gap-4">
                            {Object.keys(offlineOptions).map(key => (
                                <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        checked={offlineOptions[key]}
                                        onChange={() => setOfflineOptions(prev => ({...prev, [key]: !prev[key]}))}
                                        className="w-4 h-4 text-black focus:ring-black border-slate-400 rounded-sm"
                                    />
                                    <span className="uppercase text-sm font-semibold">{offlineLabels[key] || key}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center border-b-4 border-black pb-4 mb-8">
                        <h1 className="text-4xl font-black uppercase tracking-tighter">Mountain River Expedition</h1>
                        <p className="text-lg font-bold mt-2">Field Mission Report</p>
                        <p className="text-sm mt-1">Generated: {new Date().toLocaleDateString()} • User ID: {user.uid.substring(0, 8)}</p>
                    </div>

                    {offlineOptions.guardian && (
                        <section className="break-inside-avoid border-2 border-black p-4">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4"><Shield className="w-5 h-5"/> Emergency & Logistics Contacts</h2>
                           
                            {guardianAngels.length > 0 ? guardianAngels.map(ga => (
                                <div key={ga.id} className="grid grid-cols-2 gap-4 text-sm mb-6">
                                    <div className="col-span-2 font-bold uppercase underline">Guardian Angel (Primary Contact)</div>
                                    <div><span className="font-bold block">NAME:</span> {ga.name}</div>
                                    <div><span className="font-bold block">PHONE 1:</span> {ga.phone1}</div>
                                    <div className="col-span-2"><span className="font-bold block">ADDRESS:</span> {ga.address}</div>
                                    <div><span className="font-bold block">EMAIL 1:</span> {ga.email1}</div>
                                    <div><span className="font-bold block">PHONE 2:</span> {ga.phone2}</div>
                                </div>
                            )) : <p className="italic text-sm mb-4">No Guardian Angel recorded.</p>}

                            {serviceProviders.length > 0 && (
                                <div>
                                    <div className="font-bold uppercase underline mb-2 text-sm">Logistics Directory</div>
                                    <table className="w-full text-xs text-left">
                                        <thead>
                                            <tr className="border-b border-black">
                                                <th className="py-1">Role</th>
                                                <th className="py-1">Company/Name</th>
                                                <th className="py-1">Phone</th>
                                                <th className="py-1">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceProviders.map(sp => (
                                                <tr key={sp.id} className="border-b border-slate-300">
                                                    <td className="py-1 font-bold">{sp.role}</td>
                                                    <td className="py-1">{sp.company}</td>
                                                    <td className="py-1 font-mono">{sp.phone}</td>
                                                    <td className="py-1 italic">{sp.notes}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    )}

                    {offlineOptions.crew && (
                        <section className="break-inside-avoid">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Crew Roster & Med Info</h2>
                            <div className="grid grid-cols-1 gap-4">
                                {confirmedPeople.map(p => (
                                    <div key={p.id} className="border border-black p-3 flex flex-col md:flex-row gap-4 text-sm">
                                            <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-black pb-2 md:pb-0 md:pr-2">
                                                <div className="font-black text-lg">{p.name}</div>
                                                <div>Res: {p.address || p.residence}</div>
                                                <div>Ph: {p.phone1}</div>
                                                {p.medicalNotes && <div className="mt-2 font-bold uppercase text-xs border border-black p-1 bg-slate-200">⚠ MED/DIET: {p.medicalNotes}</div>}
                                            </div>
                                            <div className="flex-grow grid grid-cols-2 gap-2">
                                                <div><span className="font-bold">OHIP:</span> {p.ohip || 'N/A'}</div>
                                                <div><span className="font-bold">InReach:</span> {p.inReach || 'N/A'}</div>
                                                <div className="col-span-2 bg-slate-100 p-1 border border-slate-300">
                                                    <span className="font-bold text-xs uppercase block">Emergency Contact</span>
                                                    {p.emergContact} ({p.emergContactNum})
                                                </div>
                                            </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {offlineOptions.dates && (
                        <section className="break-inside-avoid border-2 border-black p-4">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Trip Dates & Timeline</h2>
                            {dateConsensus.length > 0 ? (
                                dateConsensus.map((s, i) => (
                                    <div key={i} className="mb-2">
                                        <span className="font-bold text-lg">PRIMARY WINDOW:</span> {s.startStr} to {s.endStr} ({s.duration} days)
                                    </div>
                                ))
                            ) : <p className="italic">No consensus dates found.</p>}
                        </section>
                    )}

                    {offlineOptions.travel && (
                        <section className="break-inside-avoid">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Travel Logistics</h2>
                            <table className="w-full text-xs text-left border-collapse border border-black">
                                <thead>
                                    <tr className="bg-slate-200">
                                        <th className="border border-black p-2">Name</th>
                                        <th className="border border-black p-2">Depart Home</th>
                                        <th className="border border-black p-2">Staging Arrival</th>
                                        <th className="border border-black p-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {confirmedPeople.map(p => (
                                        <tr key={p.id}>
                                            <td className="border border-black p-2 font-bold">{p.name}</td>
                                            <td className="border border-black p-2">{p.travel_departHome ? new Date(p.travel_departHome).toLocaleString('en-US', {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'}) : '-'}</td>
                                            <td className="border border-black p-2">{p.travel_stagingArrival ? new Date(p.travel_stagingArrival).toLocaleString('en-US', {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'}) : '-'}</td>
                                            <td className="border border-black p-2">{p.travel_status || 'Planned'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {offlineOptions.loads && (
                        <section className="break-inside-avoid border-2 border-black p-4 flex justify-between items-center bg-slate-50">
                            <div>
                                <div className="text-xs font-bold uppercase">Total Expedition Load</div>
                                <div className="text-2xl font-black">{safeFixed(totalExpeditionWeight, 1)} lbs</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold uppercase">Total Group Gear</div>
                                <div className="text-xl font-bold">{safeFixed(totalGroupGearWeight, 1)} lbs</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold uppercase">Avg Personal Pack</div>
                                <div className="text-xl font-bold">{safeFixed(avgPersonalGearWeight, 1)} lbs</div>
                            </div>
                            <div>
                                <div className="text-xs font-bold uppercase">Est. Food</div>
                                <div className="text-xl font-bold">{safeFixed(estimatedTotalFoodWeight, 1)} lbs</div>
                            </div>
                        </section>
                    )}

                    {offlineOptions.gear && (
                        <section>
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Gear Manifests</h2>
                           
                            <div className="mb-6">
                                <h3 className="font-bold text-lg bg-black text-white px-2 py-1 mb-2">Canoe Fleet Assignment</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {canoes.map((c, i) => (
                                        <div key={c.id} className="border border-black p-2 text-sm">
                                            <div className="font-bold uppercase mb-1">Canoe #{i+1} ({c.type} {c.length}')</div>
                                            <div className="flex justify-between border-t border-black pt-1">
                                                <span>Bow: {getPersonName(c.bowPaddler)}</span>
                                                <span>Stern: {getPersonName(c.sternPaddler)}</span>
                                            </div>
                                            <div className="text-xs mt-1">
                                                Weight: {Number(c.weight||0) + (c.spraydeck ? Number(c.sprayskirtWeight||0) : 0)} lbs
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="font-bold text-lg border-b-2 border-black mb-2">Group Gear</h3>
                                    <ul className="text-sm space-y-1">
                                        {gear.map(g => (
                                            <li key={g.id} className="flex justify-between border-b border-slate-300 pb-1">
                                                <span>{g.item}</span>
                                                <span className="font-mono">{getPersonName(g.assignedTo)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg border-b-2 border-black mb-2">Repair & First Aid</h3>
                                    <ul className="text-sm space-y-1 mb-4">
                                        {repairKit.map(r => (
                                            <li key={r.id} className="flex justify-between border-b border-slate-300 pb-1">
                                                <span>{r.item}</span>
                                                <span className="font-mono">{getPersonName(r.assignedTo)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="text-xs border border-black p-2">
                                        <div className="font-bold uppercase mb-1">First Aid Check:</div>
                                        {confirmedPeople.map(p => (
                                            <div key={p.id} className="flex justify-between">
                                                <span>{p.name}</span>
                                                <span>Pers: {p.fa_personalKit ? 'Yes' : 'No'} | Grp: {p.fa_groupKit ? 'Yes' : 'No'}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {offlineOptions.group_food && (
                        <section className="break-inside-avoid">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Group Food Inventory</h2>
                            <div className="columns-2 gap-6 text-sm">
                                {Object.keys(standardGroupFood).map(category => {
                                    const items = groupFood.filter(i => i.category === category);
                                    if (items.length === 0) return null;
                                    return (
                                        <div key={category} className="break-inside-avoid mb-4 border border-black p-2">
                                            <h4 className="font-bold uppercase border-b border-black mb-1">{category}</h4>
                                            <ul className="list-none">
                                                {items.map(item => (
                                                    <li key={item.id} className="flex justify-between items-start">
                                                        <div className="flex gap-2">
                                                            <div className={`w-4 h-4 border border-black flex-shrink-0 ${item.packed ? 'bg-black' : ''}`}></div>
                                                            <span>{item.item}</span>
                                                        </div>
                                                        <span className="font-mono text-xs">{item.weight > 0 ? `${item.weight}lb` : ''}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {offlineOptions.menu && (
                        <section className="break-before-page">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Expedition Menu Plan</h2>
                            <div className="grid grid-cols-1 gap-0 border-2 border-black">
                                {Array.from({ length: tripDuration }, (_, i) => i + 1).map(day => {
                                    const dayData = menu[`day_${day}`] || {};
                                    return (
                                        <div key={day} className="flex border-b border-black last:border-0 text-sm">
                                            <div className="w-16 bg-slate-200 flex items-center justify-center font-bold border-r border-black p-2">
                                                Day {day}
                                            </div>
                                            <div className="flex-grow grid grid-cols-5 divide-x divide-black">
                                                <div className="p-2"><span className="font-bold block text-xs">BREAKFAST</span>{dayData.breakfast_meal || '-'}</div>
                                                <div className="p-2"><span className="font-bold block text-xs">LUNCH</span>{dayData.lunch_meal || '-'}</div>
                                                <div className="p-2 bg-slate-50"><span className="font-bold block text-xs">DINNER (Chef: {getPersonName(dayData.dinner_chef)})</span>{dayData.dinner_meal || '-'}</div>
                                                <div className="p-2"><span className="font-bold block text-xs">DESSERT</span>{dayData.dessert_meal || '-'}</div>
                                                <div className="p-2"><span className="font-bold block text-xs">STAPLES</span>{dayData.staples_meal || '-'}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {offlineOptions.campfire && (
                        <section className="break-inside-avoid">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Campfire & Tasks</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="border-2 border-black p-4">
                                    <h3 className="font-bold text-lg uppercase mb-2">Role Assignments</h3>
                                    {tasks.length === 0 ? <p className="italic text-sm">No tasks assigned.</p> : (
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b-2 border-black text-left"><th className="pb-1">Role/Task</th><th className="pb-1">Assigned To</th></tr>
                                            </thead>
                                            <tbody>
                                                {tasks.map(t => (
                                                    <tr key={t.id} className="border-b border-slate-300">
                                                        <td className="py-1 font-bold">{t.role}</td>
                                                        <td className="py-1">{getPersonName(t.assignedTo)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="border-2 border-black p-4">
                                    <h3 className="font-bold text-lg uppercase mb-2">Discussion Log</h3>
                                    {topics.length === 0 ? <p className="italic text-sm">No topics recorded.</p> : (
                                        <ul className="list-disc pl-4 text-sm space-y-2">
                                            {topics.map(t => (
                                                <li key={t.id}>
                                                    <span className="font-bold">{t.issue}</span>
                                                    {t.conclusion && <span className="block italic text-slate-600">Decision: {t.conclusion}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

{offlineOptions.expenses && expenseSummary && (
    <section className="break-before-page">
        <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Expense Reconciliation</h2>
        
        <div className="border-2 border-black p-4 mb-4">
            <h3 className="font-bold text-lg mb-2">Summary</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <span className="font-bold">Total Trip Cost:</span> ${safeFixed(expenseSummary.totalTripCost, 2)}
                </div>
                <div>
                    <span className="font-bold">Number of Expenses:</span> {expenses.length}
                </div>
            </div>
            
            <h4 className="font-bold mb-2">Individual Balances:</h4>
            <table className="w-full text-sm border-collapse border border-black mb-4">
                <thead>
                    <tr className="bg-slate-200">
                        <th className="border border-black p-2">Name</th>
                        <th className="border border-black p-2">Paid</th>
                        <th className="border border-black p-2">Share</th>
                        <th className="border border-black p-2">Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {expenseSummary.balances.map(b => (
                        <tr key={b.id}>
                            <td className="border border-black p-2 font-bold">{b.name}</td>
                            <td className="border border-black p-2">${safeFixed(b.paid, 2)}</td>
                            <td className="border border-black p-2">${safeFixed(b.share, 2)}</td>
                            <td className={`border border-black p-2 font-bold ${b.balance >= 0 ? '' : 'bg-slate-200'}`}>
                                {b.balance >= 0 ? '+' : ''}${safeFixed(b.balance, 2)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <h3 className="font-bold mb-2">Settlements Needed:</h3>
            {settlements.length === 0 ? (
                <p className="italic">✓ All settled up!</p>
            ) : (
                <ul className="list-disc pl-5">
                    {settlements.map((tx, i) => (
                        <li key={i} className="mb-1">
                            <span className="font-bold">{tx.from}</span> pays <span className="font-bold">{tx.to}</span>: ${safeFixed(tx.amount, 2)}
                        </li>
                    ))}
                </ul>
            )}
        </div>

        <h3 className="font-bold text-lg mb-2">Detailed Expense Log</h3>
        <table className="w-full text-xs text-left border-collapse border border-black">
            <thead className="bg-slate-200">
                <tr>
                    <th className="border border-black p-2">Date</th>
                    <th className="border border-black p-2">Item</th>
                    <th className="border border-black p-2">Cost</th>
                    <th className="border border-black p-2">Paid By</th>
                    <th className="border border-black p-2">Method</th>
                    <th className="border border-black p-2">Receipt</th>
                    <th className="border border-black p-2">Notes</th>
                </tr>
            </thead>
            <tbody>
                {expenses.sort((a, b) => (b.date || '').localeCompare(a.date || '')).map(e => {
                    const paidByNames = Object.entries(e.paidBy || {})
                        .map(([pid, amt]) => `${getPersonName(pid)} ($${amt})`)
                        .join(', ');
                    return (
                        <tr key={e.id}>
                            <td className="border border-black p-2">{e.date || '-'}</td>
                            <td className="border border-black p-2">{e.item}</td>
                            <td className="border border-black p-2">${safeFixed(e.totalCost, 2)}</td>
                            <td className="border border-black p-2">{paidByNames || '-'}</td>
                            <td className="border border-black p-2">{e.paymentMethod || '-'}</td>
                            <td className="border border-black p-2 text-center">{e.hasReceipt ? '✓' : '-'}</td>
                            <td className="border border-black p-2 text-xs italic">{e.notes || ''}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </section>
)}
                   
                    {offlineOptions.personal_gear && (
                        <section className="break-before-page">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Personal Gear Checklists</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {confirmedPeople.map(p => {
                                    const list = personalLists[p.id]?.items || [];
                                    if (list.length === 0) return null;
                                    return (
                                        <div key={p.id} className="border border-black p-2 text-xs">
                                            <h3 className="font-bold text-lg bg-black text-white px-2 mb-2">{p.name}</h3>
                                            <div className="columns-2 gap-4">
                                                {list.map((item, i) => (
                                                    <div key={i} className="mb-1 flex gap-1 items-start break-inside-avoid">
                                                        <span className={`w-3 h-3 border border-black inline-block mt-0.5 flex-shrink-0 ${item.packed ? 'bg-black' : ''}`}></span>
                                                        <span className={item.packed ? 'line-through opacity-50' : ''}>{item.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </section>
                    )}

                    {offlineOptions.personal_menu && (
                        <section className="break-before-page">
                            <h2 className="text-xl font-black uppercase border-b-2 border-black pb-2 mb-4">Personal Menu Plans</h2>
                            <div className="grid grid-cols-1 gap-8">
                                {confirmedPeople.map(p => {
                                    const menuData = personalMenus[p.id];
                                    if (!menuData || !menuData.daily) return null;
                                    const myItems = menuData.items || [];
                                    const itemMap = new Map(myItems.map(i => [i.id, i.name]));
                                   
                                    const hasData = Array.from({length: tripDuration}, (_, i) => i+1).some(day => {
                                        const d = menuData.daily[`day_${day}`] || {};
                                        return Object.keys(d).length > 0;
                                    });
                                   
                                    if (!hasData) return null;

                                    return (
                                        <div key={p.id} className="break-inside-avoid">
                                            <h3 className="font-bold text-lg bg-black text-white px-2 mb-2">{p.name}'s Meal Plan</h3>
                                            <div className="border-2 border-black text-xs">
                                                {Array.from({ length: tripDuration }, (_, i) => i + 1).map(day => {
                                                    const dailyData = menuData.daily[`day_${day}`] || {};
                                                    return (
                                                        <div key={day} className="grid grid-cols-12 border-b border-black last:border-0">
                                                            <div className="col-span-1 bg-slate-200 font-bold flex items-center justify-center border-r border-black p-1">
                                                                Day {day}
                                                            </div>
                                                            <div className="col-span-11 grid grid-cols-6 divide-x divide-black">
                                                                {['breakfast', 'lunch', 'dinner', 'snacks', 'drinks', 'desserts'].map(meal => {
                                                                    const items = dailyData[`${meal}_items`] || [];
                                                                    const text = dailyData[meal];
                                                                    return (
                                                                        <div key={meal} className="p-1 overflow-hidden">
                                                                            <span className="font-bold uppercase text-[10px] block text-slate-500">{meal}</span>
                                                                            {items.map(id => <div key={id} className="truncate">• {itemMap.get(id)}</div>)}
                                                                            {text && <div className="italic truncate">"{text}"</div>}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </section>
                    )}

                    <div className="text-center pt-8 pb-8 text-xs font-mono opacity-50">
                        -- End of Field Report --
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
            <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col h-full z-20">
                <div className="p-6 border-b border-slate-100">
                    <h1 className="text-xl font-black tracking-tight flex items-center gap-2 text-slate-800"><MapIcon className="w-6 h-6 text-indigo-600" /> MOUNTAIN RIVER</h1>
                    <p className="text-xs text-slate-400 mt-1 font-medium">Summer 2026 Expedition</p>
                </div>
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {tabs.map(tab => ( <NavItem key={tab.id} tab={tab} isActive={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} /> ))}
                </nav>
                <div className="p-4 border-t border-slate-100 bg-slate-50 space-y-2">
                    <button onClick={() => setIsOfflineMode(true)} className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"><Clipboard className="w-4 h-4" /> Field Mode</button>
                    <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-red-600 py-2 rounded-lg text-sm font-medium transition-colors"><LogOut className="w-4 h-4" /> Sign Out</button>
                </div>
            </aside>

            <div className={`md:hidden fixed inset-0 bg-slate-900/50 z-40 transition-opacity ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMobileMenuOpen(false)}></div>
            <div className={`md:hidden fixed top-0 left-0 bottom-0 w-3/4 bg-white z-50 transform transition-transform duration-300 shadow-2xl ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="p-4 flex justify-between items-center border-b">
                    <span className="font-bold text-lg">Menu</span>
                    <button onClick={() => setIsMobileMenuOpen(false)}><CloseIcon className="w-6 h-6" /></button>
                </div>
                <nav className="p-4 space-y-2">
                    {tabs.map(tab => (
                        <button key={tab.id} onClick={() => { setActiveTab(tab.id); setIsMobileMenuOpen(false); }} className={`w-full flex items-center gap-3 p-3 rounded-lg ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600'}`}>
                            <tab.icon className="w-5 h-5" /> {tab.label}
                        </button>
                    ))}
                    <div className="h-px bg-slate-100 my-4"></div>
                    <button onClick={() => { setIsOfflineMode(true); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-3 p-3 text-slate-600">
                        <Clipboard className="w-5 h-5" /> Field Mode
                    </button>
                    <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-3 p-3 text-slate-500 hover:text-red-600">
                        <LogOut className="w-5 h-5" /> Sign Out
                    </button>
                </nav>
            </div>

            <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                <div className="md:hidden h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-10 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsMobileMenuOpen(true)}><Menu className="w-6 h-6 text-slate-600" /></button>
                        <span className="font-bold text-slate-800">{tabs.find(t => t.id === activeTab)?.label}</span>
                    </div>
                    <User className="w-6 h-6 text-slate-400 bg-slate-100 rounded-full p-1" />
                </div>

                <div className="flex-1 overflow-y-auto p-4 md:p-8 pb-24 relative">
                    <div className="max-w-6xl mx-auto">
                       
                        {activeTab === 'dashboard' && (
                            <div className="space-y-6 animate-in fade-in duration-500">
                                <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                                    <div className="relative z-10">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h2 className="text-3xl font-bold mb-2">Expedition Readiness</h2>
                                                <p className="text-indigo-100 mb-6 max-w-xl">Welcome back, Paul. You have {people.length} confirmed crew members and {canoes.length} canoes for your trip to Mountain River.</p>
                                            </div>
                                            <div className="bg-white/10 rounded-lg p-2 backdrop-blur-md">
                                                <label className="block text-xs font-bold uppercase tracking-wider opacity-70 mb-1">View Dashboard As:</label>
                                                <select
                                                    value={viewingAs}
                                                    onChange={e => setViewingAs(e.target.value)}
                                                    className="bg-white text-slate-800 text-sm p-2 rounded w-48 outline-none font-bold shadow-lg"
                                                >
                                                    <option value="">-- Overview --</option>
                                                    {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-4">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"><Calendar className="w-4 h-4" /> {tripDuration} Days</div>
                                            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4" /> {people.length} Crew</div>
                                            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"><Scale className="w-4 h-4" /> {safeFixed(totalExpeditionWeight, 0)} lbs Total</div>
                                        </div>
                                    </div>
                                    <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/10 rounded-full blur-2xl"></div>
                                </div>

                                {viewingAs ? (
                                    <div className="animate-in slide-in-from-top duration-300">
                                        {(() => {
                                            const person = confirmedPeople.find(p => p.id === viewingAs);
                                            const myGear = gear.filter(g => g.assignedTo === viewingAs);
                                            const myCanoe = canoes.find(c => c.bowPaddler === viewingAs || c.sternPaddler === viewingAs);
                                            const mySeat = myCanoe ? (myCanoe.bowPaddler === viewingAs ? 'Bow' : 'Stern') : 'Unassigned';
                                            const myTasks = tasks.filter(t => t.assignedTo === viewingAs);
                                            const myCooking = Array.from({length: tripDuration}, (_, i) => i+1).filter(day => {
                                                const d = menu[`day_${day}`];
                                                return d && (d.breakfast_chef === viewingAs || d.lunch_chef === viewingAs || d.dinner_chef === viewingAs);
                                            });
                                            const balance = expenseSummary ? expenseSummary.balances.find(b => b.id === viewingAs) : null;

                                            return (
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-2">
                                                            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><User className="w-5 h-5"/></div>
                                                            <h3 className="font-bold text-lg">My Assignments</h3>
                                                        </div>
                                                        <div className="space-y-4">
                                                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                                                                <span className="text-sm font-medium text-slate-600">Seat Assignment</span>
                                                                <span className="font-bold text-slate-900">{myCanoe ? `Canoe #${canoes.indexOf(myCanoe)+1} (${mySeat})` : 'None'}</span>
                                                            </div>
                                                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                                                                <span className="text-sm font-medium text-slate-600">Cooking Shifts</span>
                                                                <span className="font-bold text-slate-900">{myCooking.length} Days</span>
                                                            </div>
                                                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                                                                <span className="text-sm font-medium text-slate-600">Assigned Tasks</span>
                                                                <span className="font-bold text-slate-900">{myTasks.length} Active</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-2">
                                                            <div className="p-2 bg-green-50 text-green-600 rounded-lg"><Backpack className="w-5 h-5"/></div>
                                                            <h3 className="font-bold text-lg">My Gear</h3>
                                                        </div>
                                                        <div className="space-y-3">
                                                            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                                                                <div className="flex justify-between mb-2 text-xs font-bold text-green-800 uppercase">
                                                                    <span>Personal Pack Weight</span>
                                                                    <span>{safeFixed(calculatePersonalWeight(viewingAs), 1)} lbs</span>
                                                                </div>
                                                                <ProgressBar value={1} max={1} colorClass="bg-green-500"/>
                                                            </div>
                                                            <div className="text-sm">
                                                                <p className="font-bold text-slate-700 mb-2">Group Gear in your pack:</p>
                                                                {myGear.length > 0 ? (
                                                                    <ul className="space-y-1">
                                                                        {myGear.map(g => (
                                                                            <li key={g.id} className="flex justify-between text-slate-600">
                                                                                <span>{g.item}</span>
                                                                                <span className="font-mono text-xs">{g.weight}lb</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : <p className="text-slate-400 italic text-xs">No group gear assigned yet.</p>}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-2">
                                                            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><DollarSign className="w-5 h-5"/></div>
                                                            <h3 className="font-bold text-lg">My Share</h3>
                                                        </div>
                                                        {balance ? (
                                                            <div className="text-center py-6">
                                                                <p className="text-sm text-slate-500 mb-1">Current Balance</p>
                                                                <h2 className={`text-4xl font-black ${balance.balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                                    {balance.balance >= 0 ? '+' : '-'}{safeFixed(Math.abs(balance.balance), 2)}
                                                                </h2>
                                                                <p className="text-xs text-slate-400 mt-2">{balance.balance >= 0 ? 'You are owed this amount' : 'You owe this amount to the group'}</p>
                                                            </div>
                                                        ) : <p className="text-center text-slate-400 italic">No expenses yet.</p>}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                            <StatCard icon={Anchor} title="Group Gear" value={`${safeFixed(totalGroupGearWeight, 0)} lbs`} subtext={`${gear.length} items logged`} color="orange" />
                                            <StatCard icon={Coffee} title="Food Estimate" value={`${safeFixed(estimatedTotalFoodWeight, 0)} lbs`} subtext={`${foodLbsPerDay} lbs/day/person`} color="pink" />
                                            <StatCard icon={DollarSign} title="Total Budget" value={`$${safeFixed(expenses.reduce((a,c)=>a+(c.totalCost||0),0), 0)}`} subtext="Expenses tracked" color="emerald" />
                                            <StatCard icon={AlertTriangle} title="Alerts" value={confirmedPeople.filter(p=>p.medicalNotes).length} subtext="Medical notes found" color="red" />
                                        </div>

                                        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-indigo-500"/> Packing Status</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div>
                                                    <div className="flex justify-between text-xs mb-1 font-medium text-slate-600"><span>Personal Gear</span><span>{Math.round(packingStats.personalGear.percent)}%</span></div>
                                                    <ProgressBar value={packingStats.personalGear.packed} max={packingStats.personalGear.total} colorClass="bg-green-500" />
                                                </div>
                                                <div>
                                                    <div className="flex justify-between text-xs mb-1 font-medium text-slate-600"><span>Personal Menu</span><span>{Math.round(packingStats.personalMenu.percent)}%</span></div>
                                                    <ProgressBar value={packingStats.personalMenu.packed} max={packingStats.personalMenu.total} colorClass="bg-teal-500" />
                                                </div>
                                                <div>
                                                    <div className="flex justify-between text-xs mb-1 font-medium text-slate-600"><span>Group Food</span><span>{Math.round(packingStats.groupFood.percent)}%</span></div>
                                                    <ProgressBar value={packingStats.groupFood.packed} max={packingStats.groupFood.total} colorClass="bg-pink-500" />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><CheckSquare className="w-5 h-5 text-indigo-500"/> Active Tasks</h3>
                                                <div className="space-y-3">
                                                    {tasks.length === 0 && <p className="text-sm text-slate-400 italic">No active tasks.</p>}
                                                    {tasks.map(t => (
                                                        <div key={t.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                            <div className={`w-2 h-2 rounded-full ${t.assignedTo ? 'bg-green-500' : 'bg-red-400'}`}></div>
                                                            <span className="text-sm font-medium text-slate-700 flex-grow">{t.role}</span>
                                                            <span className="text-xs text-slate-400 bg-white px-2 py-1 rounded border">{getPersonName(t.assignedTo)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Flame className="w-5 h-5 text-red-500"/> Campfire Discussion</h3>
                                                <div className="space-y-3">
                                                    {topics.length === 0 && <p className="text-sm text-slate-400 italic">No topics raised yet.</p>}
                                                    {topics.slice(0, 4).map(t => (
                                                        <div key={t.id} className="p-3 border-l-2 border-red-400 bg-red-50 rounded-r-lg">
                                                            <p className="text-sm font-medium text-slate-800">{t.issue}</p>
                                                            {t.conclusion && <p className="text-xs text-slate-500 mt-1 italic">Decision: {t.conclusion}</p>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'team' && (
                            <div className="space-y-6">
                                <SectionHeader title="The Crew" icon={Users} color="blue" action={
                                    <button onClick={addPerson} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 flex items-center gap-2"><Plus className="w-4 h-4"/> Add Member</button>
                                }/>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {people.map(p => (
                                        <div key={p.id} className={`bg-white rounded-xl border-l-4 shadow-sm p-5 transition-shadow hover:shadow-md ${p.confirmed ? 'border-l-green-500' : 'border-l-slate-300'}`}>
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${p.confirmed ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>{p.name ? p.name.charAt(0).toUpperCase() : '?'}</div>
                                                    <div>
                                                        <input className="font-bold text-lg text-slate-800 bg-transparent border-none p-0 focus:ring-0 placeholder:text-slate-300 w-full" value={p.name || ''} onChange={e => updatePerson(p.id, 'name', e.target.value)} placeholder="Name" />
                                                        <button onClick={() => updatePerson(p.id, 'confirmed', !p.confirmed)} className={`text-xs px-2 py-0.5 rounded-full border mt-1 ${p.confirmed ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>{p.confirmed ? 'Confirmed' : 'Pending'}</button>
                                                    </div>
                                                </div>
                                                <button onClick={() => removePerson(p.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                                            </div>
                                            <div className="space-y-3">
                                                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                                                    <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400"/><input className="bg-transparent text-sm w-full border-b border-transparent focus:border-blue-300 focus:outline-none" value={p.email || ''} onChange={e => updatePerson(p.id, 'email', e.target.value)} placeholder="Email Address"/></div>
                                                    <div className="flex items-center gap-2"><div className="w-4 flex justify-center text-slate-400">#</div><input className="bg-transparent text-sm w-full border-b border-transparent focus:border-blue-300 focus:outline-none" value={p.phone1 || ''} onChange={e => updatePerson(p.id, 'phone1', e.target.value)} placeholder="Phone Number"/></div>
                                                    <div className="flex items-center gap-2"><div className="w-4 flex justify-center text-slate-400"><MapIcon className="w-3 h-3"/></div><input className="bg-transparent text-sm w-full border-b border-transparent focus:border-blue-300 focus:outline-none" value={p.address || ''} onChange={e => updatePerson(p.id, 'address', e.target.value)} placeholder="Address"/></div>
                                                    <div className="flex items-center gap-2"><div className="w-4 flex justify-center text-slate-400"><Wind className="w-3 h-3"/></div><input className="bg-transparent text-sm w-full border-b border-transparent focus:border-blue-300 focus:outline-none" value={p.inReach || ''} onChange={e => updatePerson(p.id, 'inReach', e.target.value)} placeholder="inReach Address"/></div>
                                                </div>
                                                <div className={`rounded-lg p-3 space-y-2 border ${p.medicalNotes ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
                                                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mb-1"><Shield className="w-3 h-3"/> Safety & Medical</div>
                                                    <input className="w-full text-sm bg-transparent border-b border-slate-200 focus:border-red-400 focus:outline-none pb-1" value={p.medicalNotes || ''} onChange={e => updatePerson(p.id, 'medicalNotes', e.target.value)} placeholder="Allergies / Medical Conditions"/>
                                                    <input className="w-full text-sm bg-transparent border-b border-slate-200 focus:border-red-400 focus:outline-none pb-1" value={p.ohip || ''} onChange={e => updatePerson(p.id, 'ohip', e.target.value)} placeholder="OHIP Number"/>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <input className="text-xs bg-slate-50 p-2 rounded" value={p.emergContact || ''} onChange={e => updatePerson(p.id, 'emergContact', e.target.value)} placeholder="Emerg. Contact"/>
                                                        <input className="text-xs bg-slate-50 p-2 rounded" value={p.emergContactNum || ''} onChange={e => updatePerson(p.id, 'emergContactNum', e.target.value)} placeholder="Emerg. Phone"/>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'campfire' && (
                            <div className="space-y-6">
                                <SectionHeader title="Campfire & Tasks" icon={Flame} color="red" />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                            <div className="flex justify-between mb-4"><h3 className="font-bold text-slate-800">Discussion Topics</h3><button onClick={addTopic} className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded"><Plus className="w-4 h-4 inline"/> Topic</button></div>
                                            <div className="space-y-4">
                                                {topics.map(t => (
                                                    <div key={t.id} className="border-l-4 border-red-400 bg-red-50/50 pl-4 py-2">
                                                        <input value={t.issue} onChange={e=>updateTopic(t.id, 'issue', e.target.value)} className="font-bold text-slate-800 bg-transparent w-full mb-1" placeholder="Topic..."/>
                                                        <textarea value={t.conclusion} onChange={e=>updateTopic(t.id, 'conclusion', e.target.value)} className="text-sm text-slate-600 bg-transparent w-full resize-none h-16" placeholder="Conclusion/Notes..."></textarea>
                                                        <div className="flex justify-between mt-2">
                                                            <select value={t.raisedBy || ''} onChange={e=>updateTopic(t.id, 'raisedBy', e.target.value)} className="text-xs bg-white border rounded p-1"><option value="">Raised By...</option>{confirmedPeople.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
                                                            <button onClick={()=>removeTopic(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3"/></button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                            <div className="flex justify-between mb-4"><h3 className="font-bold text-slate-800">Expedition Tasks</h3><button onClick={addTask} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded"><Plus className="w-4 h-4 inline"/> Task</button></div>
                                            <div className="space-y-2">
                                                {tasks.map(t => (
                                                    <div key={t.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-100 group">
                                                        <select value={t.assignedTo || ''} onChange={e=>updateTask(t.id, 'assignedTo', e.target.value)} className={`text-xs p-1 rounded border ${t.assignedTo ? 'bg-green-100 text-green-800 border-green-200' : 'bg-white text-slate-400'}`}>
                                                            <option value="">Who?</option>
                                                            {confirmedPeople.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                                                        </select>
                                                        <input value={t.role} onChange={e=>updateTask(t.id, 'role', e.target.value)} className="flex-grow bg-transparent text-sm" placeholder="Task description..."/>
                                                        <button onClick={()=>removeTask(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3"/></button>
                                                    </div>
                                                ))}
                                            </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'dates' && (
                            <div className="space-y-6">
                                <SectionHeader title="Trip Dates & Availability" icon={Calendar} color="purple" />
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="lg:col-span-2 space-y-4">
                                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                                <h3 className="font-bold text-slate-800 mb-4">Add Date Proposal</h3>
                                                <div className="flex flex-wrap gap-3 items-end">
                                                    <div className="flex-1 min-w-[120px]"><label className="text-xs font-bold text-slate-400 block mb-1">Who</label><select value={newDateProposal.whoId} onChange={e => setNewDateProposal({...newDateProposal, whoId: e.target.value})} className="w-full border rounded p-2 text-sm"><option value="">Select...</option>{confirmedPeople.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                                                    <div className="flex-1 min-w-[120px]"><label className="text-xs font-bold text-slate-400 block mb-1">Start</label><input type="date" value={newDateProposal.startDate} onChange={e => setNewDateProposal({...newDateProposal, startDate: e.target.value})} className="w-full border rounded p-2 text-sm"/></div>
                                                    <div className="flex-1 min-w-[120px]"><label className="text-xs font-bold text-slate-400 block mb-1">End</label><input type="date" value={newDateProposal.endDate} onChange={e => setNewDateProposal({...newDateProposal, endDate: e.target.value})} className="w-full border rounded p-2 text-sm"/></div>
                                                    <button onClick={addDateProposal} className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold shadow-md hover:bg-purple-700 h-10 flex items-center justify-center"><Plus className="w-5 h-5"/></button>
                                                </div>
                                            </div>
                                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                                <h3 className="font-bold text-slate-800 mb-4">Proposed Windows</h3>
                                                {people.map(p => {
                                                    const props = dates[p.id] || [];
                                                    if(props.length === 0) return null;
                                                    return (
                                                        <div key={p.id} className="mb-4 last:mb-0 border-b border-slate-100 pb-3 last:border-0">
                                                            <h4 className="font-bold text-slate-700 text-sm mb-2">{p.name}</h4>
                                                            <div className="space-y-2">
                                                                {props.map(d => (
                                                                    <div key={d.id} className="flex justify-between items-center bg-purple-50 p-2 rounded-lg text-sm">
                                                                        <div className="flex gap-4">
                                                                            <span className="font-mono font-semibold text-purple-700">{d.startDate} <span className="text-purple-300">→</span> {d.endDate}</span>
                                                                            <span className="text-slate-500">({calculateDays(d.startDate, d.endDate)} days)</span>
                                                                        </div>
                                                                        <button onClick={() => removeDate(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                    </div>
                                    <div>
                                            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg">
                                                <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5"/> Consensus</h3>
                                                {dateConsensus.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {dateConsensus.slice(0, 3).map((seg, i) => (
                                                            <div key={i} className="bg-white/10 backdrop-blur-sm p-3 rounded-lg border border-white/20">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="font-bold text-lg">{seg.count}/{people.length} Crew</span>
                                                                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded text-white font-medium">{seg.duration} Days</span>
                                                                </div>
                                                                <div className="text-sm opacity-90">{seg.startStr} — {seg.endStr}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : <p className="opacity-70 text-sm italic">Enter dates to see overlap.</p>}
                                            </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'travel' && (
                            <div className="space-y-6 pb-24 relative">
                                <div className="flex justify-between items-center mb-4">
                                    <SectionHeader title="Expedition Logistics" icon={Plane} color="sky" />
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setIsWeatherSidebarOpen(!isWeatherSidebarOpen)}
                                            className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all flex items-center gap-2 ${isWeatherSidebarOpen ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200'}`}
                                        >
                                            <Cloud className="w-4 h-4"/> Weather Hub
                                        </button>
                                        <button
                                            onClick={() => setIsLogisticsEditing(!isLogisticsEditing)}
                                            className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all flex items-center gap-2 ${isLogisticsEditing ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-600 border-indigo-200'}`}
                                        >
                                            {isLogisticsEditing ? <><SaveAll className="w-4 h-4"/> Save Leg</> : <><Edit2 className="w-4 h-4"/> Edit Leg</>}
                                        </button>
                                    </div>
                                </div>

                                {/* Logistics Traffic Lights */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                    <TrafficLight
                                        label="Commercial Flights"
                                        status={confirmedPeople.every(p => p.travel_departHome && p.travel_stagingArrival) ? 'green' : 'yellow'}
                                        subtext={`${confirmedPeople.filter(p => p.travel_departHome).length}/${confirmedPeople.length} Confirmed`}
                                        icon={Plane}
                                    />
                                    <TrafficLight
                                        label="Staging Hotel"
                                        status={logistics.staging_hotel && logistics.staging_stay_start ? 'green' : 'red'}
                                        subtext={logistics.staging_hotel || 'Not Booked'}
                                        icon={Bed}
                                    />
                                    <TrafficLight
                                        label="Air Charter"
                                        status={logistics.char_status === 'Go' ? 'green' : 'yellow'}
                                        subtext={`Payload: ${Math.round(totalExpeditionWeight)}/${logistics.charter_max_load || '??'} lbs`}
                                        icon={Navigation}
                                    />
                                    <TrafficLight
                                        label="Watershuttle"
                                        status={logistics.shuttle_return_gps ? 'green' : 'red'}
                                        subtext={logistics.shuttle_return_gps ? 'Coords Ready' : 'Coords Needed'}
                                        icon={Anchor}
                                    />
                                </div>

                                {/* Phase Toggle */}
                                <div className="flex bg-slate-100 p-1 rounded-xl w-fit mb-6 overflow-x-auto max-w-full">
                                    <button onClick={() => setLogisticsPhase('staging')} className={`whitespace-nowrap px-6 py-2 rounded-lg text-sm font-black transition-all ${logisticsPhase === 'staging' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>1. THE APPROACH</button>
                                    <button onClick={() => setLogisticsPhase('approach')} className={`whitespace-nowrap px-6 py-2 rounded-lg text-sm font-black transition-all ${logisticsPhase === 'approach' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>2. STAGING</button>
                                    <button onClick={() => setLogisticsPhase('exit')} className={`whitespace-nowrap px-6 py-2 rounded-lg text-sm font-black transition-all ${logisticsPhase === 'exit' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>3. THE EXIT</button>
                                    <button onClick={() => setLogisticsPhase('homestretch')} className={`whitespace-nowrap px-6 py-2 rounded-lg text-sm font-black transition-all ${logisticsPhase === 'homestretch' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>4. HOME STRETCH</button>
                                    <button onClick={() => setLogisticsPhase('directory')} className={`whitespace-nowrap px-6 py-2 rounded-lg text-sm font-black transition-all ${logisticsPhase === 'directory' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}>5. DIRECTORY</button>
                                </div>

                                <div className="flex flex-col xl:flex-row gap-6">
                                    {/* Main Planning Area */}
                                    <div className="flex-1 space-y-6">
                                       
                                        {logisticsPhase === 'staging' && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-sky-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Plane className="w-6 h-6"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Commercial Flight Coordination</h3>
                                                        </div>
                                                        <div className="text-xs font-bold bg-white/20 px-2 py-1 rounded uppercase">Approach Stage</div>
                                                    </div>
                                                    <div className="p-0 overflow-x-auto">
                                                        <table className="w-full text-left border-collapse">
                                                            <thead className="bg-slate-50 text-[10px] uppercase font-black text-slate-400 border-b border-slate-100">
                                                                <tr>
                                                                    <th className="p-4">Crew Member</th>
                                                                    <th className="p-4">Depart Date/Time</th>
                                                                    <th className="p-4">Airline</th>
                                                                    <th className="p-4">Flight #</th>
                                                                    <th className="p-4">Landing Time</th>
                                                                    <th className="p-4">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-50">
                                                                {confirmedPeople.map(p => (
                                                                    <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                                                                        <td className="p-4">
                                                                            <div className="font-bold text-slate-800">{p.name}</div>
                                                                            <div className="text-[10px] text-slate-400 uppercase font-bold">{p.email || 'No email'}</div>
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="datetime-local"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.travel_departHome || ''}
                                                                                    onChange={e => updatePerson(p.id, 'travel_departHome', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm text-slate-600">{p.travel_departHome ? new Date(p.travel_departHome).toLocaleString([], {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'}) : '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="Airline"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500 font-medium"
                                                                                    value={p.travel_airline || ''}
                                                                                    onChange={e => updatePerson(p.id, 'travel_airline', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm font-medium text-slate-700">{p.travel_airline || '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4 font-mono text-sm">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="AC123"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500 uppercase"
                                                                                    value={p.travel_flightNum || ''}
                                                                                    onChange={e => updatePerson(p.id, 'travel_flightNum', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                p.travel_flightNum || '--'
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="time"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.travel_stagingArrival || ''}
                                                                                    onChange={e => updatePerson(p.id, 'travel_stagingArrival', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm text-slate-600">{p.travel_stagingArrival || '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <select
                                                                                    className="text-[10px] border rounded p-1 w-full font-bold uppercase outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.travel_status || 'Planned'}
                                                                                    onChange={e => updatePerson(p.id, 'travel_status', e.target.value)}
                                                                                >
                                                                                    <option>Planned</option>
                                                                                    <option>Booked</option>
                                                                                    <option>Arrived</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full border ${
                                                                                    p.travel_status === 'Arrived' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                                                    p.travel_status === 'Booked' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                                                                                    'bg-slate-50 text-slate-500 border-slate-200'
                                                                                }`}>
                                                                                    {p.travel_status || 'Planned'}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {logisticsPhase === 'approach' && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-left-4 duration-300">
                                                {/* Parent: Bush Plane Charter */}
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-indigo-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Plane className="w-6 h-6"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Bush Plane Charter (Takeoff)</h3>
                                                        </div>
                                                        <div className="text-xs font-bold bg-white/20 px-2 py-1 rounded">DEPARTURE LEG</div>
                                                    </div>
                                                    <div className="p-6">
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6 pb-6 border-b border-slate-100">
                                                            <EditableLogisticsField label="Base Location" value={logistics.charter_base} field="charter_base" placeholder="Northway Dock" icon={MapPin} />
                                                            <EditableLogisticsField label="Takeoff Time" value={logistics.takeoff_time} field="takeoff_time" type="time" icon={Clock} />
                                                            <EditableLogisticsField label="Max Payload" value={logistics.charter_max_load} field="charter_max_load" type="number" placeholder="1200 lbs" icon={Scale} />
                                                            <EditableLogisticsField label="Charter Status" value={logistics.charter_status} field="charter_status" placeholder="Checking..." icon={Shield} />
                                                        </div>
                                                        {/* Child: Manifest */}
                                                        <div>
                                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Manifest: Crew at Staging</h4>
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                                {confirmedPeople.map(p => (
                                                                    <div key={p.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                                                                        <div className={`w-2 h-2 rounded-full ${p.travel_status === 'Arrived' ? 'bg-emerald-500' : 'bg-amber-400'}`}></div>
                                                                        <span className="text-xs font-bold text-slate-700">{p.name}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Parent: Staging Hotel */}
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-slate-800 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Bed className="w-6 h-6"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Staging Basecamp (Hotel)</h3>
                                                        </div>
                                                    </div>
                                                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        <EditableLogisticsField label="Hotel Name" value={logistics.staging_hotel} field="staging_hotel" placeholder="The Mackenzie Inn" icon={MapPin} />
                                                        <EditableLogisticsField label="Phone" value={logistics.hotel_phone} field="hotel_phone" placeholder="867-555-0199" icon={Phone} />
                                                        <EditableLogisticsField label="Cost Estimate" value={logistics.hotel_cost} field="hotel_cost" type="number" placeholder="$0.00" icon={DollarSign} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {logisticsPhase === 'exit' && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                                {/* Parent: Watershuttle */}
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-blue-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Ship className="w-6 h-6"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Watershuttle (Extraction)</h3>
                                                        </div>
                                                    </div>
                                                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        <EditableLogisticsField label="Pickup Location" value={logistics.shuttle_return_loc} field="shuttle_return_loc" placeholder="End of Canyon" icon={MapPin} />
                                                        <EditableLogisticsField label="GPS Coordinates" value={logistics.shuttle_return_gps} field="shuttle_return_gps" placeholder="62.12, -128.56" icon={Navigation} />
                                                        <EditableLogisticsField label="Scheduled Time" value={logistics.shuttle_return_pickup_time} field="shuttle_return_pickup_time" type="time" icon={Clock} />
                                                    </div>
                                                </div>

                                                {/* Parent: Return Charter */}
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-orange-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Plane className="w-6 h-6 rotate-180"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Charter Homebound</h3>
                                                        </div>
                                                    </div>
                                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <EditableLogisticsField label="Pickup DateTime" value={logistics.charter_return_datetime} field="charter_return_datetime" type="datetime-local" icon={Calendar} />
                                                        <EditableLogisticsField label="Extraction GPS" value={logistics.charter_return_gps} field="charter_return_gps" placeholder="Coordinates" icon={Navigation} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {logisticsPhase === 'homestretch' && (
                                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                                {/* Parent: Commercial Flight Coordination (Return) */}
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-sky-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Plane className="w-6 h-6 rotate-180"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Commercial Flight Coordination</h3>
                                                        </div>
                                                        <div className="text-xs font-bold bg-white/20 px-2 py-1 rounded uppercase">Return Stage</div>
                                                    </div>
                                                    <div className="p-0 overflow-x-auto">
                                                        <table className="w-full text-left border-collapse">
                                                            <thead className="bg-slate-50 text-[10px] uppercase font-black text-slate-400 border-b border-slate-100">
                                                                <tr>
                                                                    <th className="p-4">Crew Member</th>
                                                                    <th className="p-4">Depart Date/Time</th>
                                                                    <th className="p-4">Airline</th>
                                                                    <th className="p-4">Flight #</th>
                                                                    <th className="p-4">Arrival Home</th>
                                                                    <th className="p-4">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-50">
                                                                {confirmedPeople.map(p => (
                                                                    <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                                                                        <td className="p-4">
                                                                            <div className="font-bold text-slate-800">{p.name}</div>
                                                                            <div className="text-[10px] text-slate-400 uppercase font-bold">{p.email || 'No email'}</div>
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="datetime-local"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.return_departDate || ''}
                                                                                    onChange={e => updatePerson(p.id, 'return_departDate', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm text-slate-600">{p.return_departDate ? new Date(p.return_departDate).toLocaleString([], {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'}) : '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="Airline"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500 font-medium"
                                                                                    value={p.return_airline || ''}
                                                                                    onChange={e => updatePerson(p.id, 'return_airline', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm font-medium text-slate-700">{p.return_airline || '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4 font-mono text-sm">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="AC123"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500 uppercase"
                                                                                    value={p.return_flightNum || ''}
                                                                                    onChange={e => updatePerson(p.id, 'return_flightNum', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                p.return_flightNum || '--'
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <input
                                                                                    type="time"
                                                                                    className="text-xs border rounded p-1 w-full outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.return_arrivalHome || ''}
                                                                                    onChange={e => updatePerson(p.id, 'return_arrivalHome', e.target.value)}
                                                                                />
                                                                            ) : (
                                                                                <span className="text-sm text-slate-600">{p.return_arrivalHome || '--'}</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="p-4">
                                                                            {isLogisticsEditing ? (
                                                                                <select
                                                                                    className="text-[10px] border rounded p-1 w-full font-bold uppercase outline-none focus:ring-1 focus:ring-sky-500"
                                                                                    value={p.return_status || 'Planned'}
                                                                                    onChange={e => updatePerson(p.id, 'return_status', e.target.value)}
                                                                                >
                                                                                    <option>Planned</option>
                                                                                    <option>Booked</option>
                                                                                    <option>Home</option>
                                                                                </select>
                                                                            ) : (
                                                                                <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full border ${
                                                                                    p.return_status === 'Home' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                                                    p.return_status === 'Booked' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                                                                                    'bg-slate-50 text-slate-500 border-slate-200'
                                                                                }`}>
                                                                                    {p.return_status || 'Planned'}
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-5 bg-emerald-600 text-white flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <Wind className="w-6 h-6"/>
                                                            <h3 className="font-black text-lg tracking-tight uppercase">Home Stretch (Final Travel)</h3>
                                                        </div>
                                                    </div>
                                                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <EditableLogisticsField label="Return to Hub Date" value={logistics.return_hub_date} field="return_hub_date" type="datetime-local" icon={Calendar} />
                                                        <EditableLogisticsField label="Notes/Post-Trip" value={logistics.homestretch_notes} field="homestretch_notes" placeholder="Final flight details..." icon={FileText} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {logisticsPhase === 'directory' && (
                                            <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                                                <SectionHeader title="Service Providers" icon={Bus} action={<button onClick={addServiceProvider} className="bg-slate-800 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-md hover:bg-slate-900 flex items-center gap-1"><Plus className="w-4 h-4"/> Add Provider</button>} />
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {serviceProviders.map(sp => (
                                                        <div key={sp.id} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm group hover:border-indigo-300 transition-colors">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <input value={sp.role} onChange={e=>updateServiceProvider(sp.id, 'role', e.target.value)} className="text-[10px] font-black uppercase text-indigo-400 bg-transparent outline-none" placeholder="ROLE"/>
                                                                <button onClick={()=>removeServiceProvider(sp.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                                                            </div>
                                                            <input value={sp.company} onChange={e=>updateServiceProvider(sp.id, 'company', e.target.value)} className="font-bold text-slate-800 w-full outline-none mb-2" placeholder="Company Name"/>
                                                            <div className="flex items-center gap-3">
                                                                <Phone className="w-4 h-4 text-slate-400"/>
                                                                <input value={sp.phone} onChange={e=>updateServiceProvider(sp.id, 'phone', e.target.value)} className="text-sm text-slate-600 font-mono flex-1 outline-none" placeholder="867..."/>
                                                                <a href={`tel:${sp.phone}`} className="p-2 bg-slate-100 rounded-lg"><Phone className="w-3 h-3"/></a>
                                                            </div>
                                                            <div className="mt-4 pt-4 border-t border-slate-50 flex items-center gap-2">
                                                                <span className="text-[10px] font-bold text-slate-400 uppercase">Est. Cost:</span>
                                                                <input type="number" value={sp.cost || ''} onChange={e=>updateServiceProvider(sp.id, 'cost', e.target.value)} className="w-20 text-xs font-mono bg-slate-50 p-1 rounded" placeholder="0.00"/>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Group Logistics Budget Card */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-lg mt-8 flex flex-col md:flex-row justify-between items-center gap-6">
                                            <div className="flex items-center gap-4">
                                                <div className="p-3 bg-emerald-100 text-emerald-700 rounded-xl"><DollarSign className="w-6 h-6"/></div>
                                                <div>
                                                    <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest">Est. Logistics Budget</h4>
                                                    <div className="text-2xl font-black text-slate-800">
                                                        ${safeFixed(
                                                            (parseFloat(logistics.hotel_cost) || 0) +
                                                            serviceProviders.reduce((acc, sp) => acc + (parseFloat(sp.cost) || 0), 0),
                                                            2
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => pushToExpense('Staging Hotel', logistics.hotel_cost, 'Pre-trip accommodation')}
                                                    className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-200 transition-colors"
                                                >
                                                    Push Hotel to Expenses
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        serviceProviders.forEach(sp => {
                                                            if(sp.cost > 0) pushToExpense(`${sp.role}: ${sp.company}`, sp.cost, 'Logistics fee');
                                                        });
                                                    }}
                                                    className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 shadow-md transition-colors"
                                                >
                                                    Push All Providers
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Weather Operations Sidebar */}
                                    {isWeatherSidebarOpen && (
                                        <div className="w-full xl:w-72 h-full flex-shrink-0 animate-in slide-in-from-right-8 duration-300">
                                            <WeatherOperationsHub isSidebar={true} onToggle={() => setIsWeatherSidebarOpen(false)} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'gear' && (
                            <div className="space-y-8">
                                <SectionHeader title="Canoe Fleet & Gear" icon={Anchor} color="orange" />
                               
                                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                    <div className="p-4 bg-orange-50 border-b border-orange-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <Ship className="w-5 h-5 text-orange-800"/>
                                                <h3 className="font-bold text-orange-800">Canoe Fleet</h3>
                                            </div>
                                            <div className="flex gap-4 text-xs font-bold uppercase text-orange-800/70">
                                                <span>Hull Total: {safeFixed(totalHullWeight, 0)} lbs</span>
                                                <span>Skirt Total: {safeFixed(totalSprayskirtWeight, 0)} lbs</span>
                                                <span className="text-orange-900 border-l border-orange-200 pl-4">Fleet Total: {safeFixed(totalCanoeWeight, 0)} lbs</span>
                                            </div>
                                            <button onClick={addCanoe} className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-sm hover:bg-orange-700 flex items-center gap-2">
                                                <Plus className="w-4 h-4"/> Add Canoe
                                            </button>
                                    </div>
                                   
                                    <div className="divide-y divide-slate-100">
                                            {canoes.length === 0 && <div className="p-8 text-center text-slate-400 italic">No canoes added yet. Add one to start planning the fleet.</div>}
                                            {canoes.map((canoe, index) => (
                                                <div key={canoe.id} className="p-4 hover:bg-slate-50 transition-colors">
                                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                       
                                                        <div className="md:col-span-4 grid grid-cols-2 gap-2">
                                                            <div className="col-span-2">
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase">Canoe Name / ID</label>
                                                                <div className="flex justify-between items-center">
                                                                    <input
                                                                        value={canoe.name || ''}
                                                                        onChange={e => updateCanoe(canoe.id, 'name', e.target.value)}
                                                                        className="w-full font-bold text-slate-800 border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent"
                                                                        placeholder={`Canoe #${index + 1}`}
                                                                    />
                                                                    <span className="text-xs font-bold text-slate-500 whitespace-nowrap ml-2">Total: {Number(canoe.weight || canoe.hullWeight || 0) + (canoe.spraydeck ? Number(canoe.sprayskirtWeight || 0) : 0)} lbs</span>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase">Make/Model</label>
                                                                <input
                                                                    value={canoe.make || ''}
                                                                    onChange={e => updateCanoe(canoe.id, 'make', e.target.value)}
                                                                    className="w-full text-sm border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent"
                                                                    placeholder="e.g. Prospector"
                                                                />
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <div>
                                                                    <label className="text-[10px] font-bold text-slate-400 uppercase">Len (ft)</label>
                                                                    <input
                                                                        type="number"
                                                                        value={canoe.length || ''}
                                                                        onChange={e => updateCanoe(canoe.id, 'length', e.target.value)}
                                                                        className="w-full text-sm border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent"
                                                                        placeholder="16"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] font-bold text-slate-400 uppercase">Hull (lb)</label>
                                                                    <input
                                                                        type="number"
                                                                        value={canoe.weight || ''}
                                                                        onChange={e => updateCanoe(canoe.id, 'weight', e.target.value)}
                                                                        className="w-full text-sm border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent"
                                                                        placeholder="50"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="md:col-span-4 space-y-2">
                                                            <div className="flex items-center gap-4">
                                                                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={canoe.spraydeck || false}
                                                                        onChange={e => updateCanoe(canoe.id, 'spraydeck', e.target.checked)}
                                                                        className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500"
                                                                    />
                                                                    <span>Sprayskirt?</span>
                                                                </label>
                                                                {canoe.spraydeck && (
                                                                    <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                                                                        <span className="text-[10px] font-black text-slate-400 uppercase">Skirt (lb):</span>
                                                                        <input
                                                                            type="number"
                                                                            step="0.1"
                                                                            value={canoe.sprayskirtWeight || ''}
                                                                            onChange={e => updateCanoe(canoe.id, 'sprayskirtWeight', e.target.value)}
                                                                            className="w-12 text-sm border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent text-center"
                                                                            placeholder="0"
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div className="flex items-center gap-2 ml-auto">
                                                                    <span className="text-xs font-bold text-slate-400 uppercase">Paddles:</span>
                                                                    <input
                                                                        type="number"
                                                                        value={canoe.paddleCount || 2}
                                                                        onChange={e => updateCanoe(canoe.id, 'paddleCount', e.target.value)}
                                                                        className="w-12 text-sm border rounded px-1 py-0.5"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase">Misc / Notes</label>
                                                                <input
                                                                    value={canoe.notes || ''}
                                                                    onChange={e => updateCanoe(canoe.id, 'notes', e.target.value)}
                                                                    className="w-full text-sm border-b border-slate-200 focus:border-orange-400 outline-none bg-transparent"
                                                                    placeholder="e.g. Needs yoke pad repair..."
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="md:col-span-3 space-y-2">
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase block">Bow Paddler</label>
                                                                <select
                                                                    value={canoe.bowPaddler || ''}
                                                                    onChange={e => updateCanoe(canoe.id, 'bowPaddler', e.target.value)}
                                                                    className="w-full text-sm border rounded bg-white py-1 px-2"
                                                                >
                                                                    <option value="">-- Unassigned --</option>
                                                                    {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase block">Stern Paddler</label>
                                                                <select
                                                                    value={canoe.sternPaddler || ''}
                                                                    onChange={e => updateCanoe(canoe.id, 'sternPaddler', e.target.value)}
                                                                    className="w-full text-sm border rounded bg-white py-1 px-2"
                                                                >
                                                                    <option value="">-- Unassigned --</option>
                                                                    {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div className="md:col-span-1 flex justify-end items-start pt-2">
                                                            <button onClick={() => removeCanoe(canoe.id)} className="text-slate-300 hover:text-red-500 p-2 rounded hover:bg-slate-100 transition-colors" title="Remove Canoe">
                                                                <Trash2 className="w-5 h-5"/>
                                                            </button>
                                                        </div>

                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                    <div className="bg-slate-50 p-4 border-b border-slate-200 flex justify-between items-center font-bold text-slate-700">
                                        <span>Group Gear Manifest ({safeFixed(totalGroupGearWeight, 1)} lbs)</span>
                                        <button onClick={addGear} className="bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-50 flex items-center gap-2"><Plus className="w-4 h-4"/> Add Gear</button>
                                    </div>
                                    <table className="w-full text-left text-sm">
                                            <thead className="bg-slate-50 text-slate-500 font-medium"><tr><th className="p-3 w-1/3">Item</th><th className="p-3">Category</th><th className="p-3 w-24">Weight</th><th className="p-3">Assigned To</th><th className="p-3 w-10"></th></tr></thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {gear.map(item => (
                                                    <tr key={item.id} className="group hover:bg-slate-50">
                                                        <td className="p-2"><input value={item.item || ''} onChange={(e) => updateGear(item.id, 'item', e.target.value)} className="w-full bg-transparent p-1 rounded" placeholder="Item"/></td>
                                                        <td className="p-2"><select value={item.category || ''} onChange={(e) => updateGear(item.id, 'category', e.target.value)} className="bg-transparent text-xs"><option>General</option><option>Kitchen</option><option>Shelter</option><option>Safety</option></select></td>
                                                        <td className="p-2"><input type="number" value={item.weight || 0} onChange={(e) => updateGear(item.id, 'weight', e.target.value)} className="w-16 bg-transparent text-right" /></td>
                                                        <td className="p-2"><select value={item.assignedTo || ''} onChange={(e) => updateGear(item.id, 'assignedTo', e.target.value)} className="bg-transparent text-xs w-full"><option value="">Unassigned</option>{confirmedPeople.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></td>
                                                        <td className="p-2"><button onClick={() => removeGear(item.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4"/></button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                    </table>
                                </div>
                                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                    <div className="bg-red-50 p-4 border-b border-red-100 font-bold text-red-800 flex justify-between"><span>Safety & Repair</span> <button onClick={addRepairItem} className="text-xs bg-white border border-red-200 px-2 py-1 rounded">+ Add Repair Item</button></div>
                                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Repair Kit</h4>
                                                {repairKit.map(item => (
                                                    <div key={item.id} className="flex gap-2 items-center mb-1 text-sm border-b border-slate-50 pb-1">
                                                        <input value={item.item} onChange={e=>updateRepairItem(item.id, 'item', e.target.value)} className="flex-grow bg-transparent" placeholder="Item..."/>
                                                        <select value={item.assignedTo || ''} onChange={e=>updateRepairItem(item.id, 'assignedTo', e.target.value)} className="text-xs w-24"><option value="">Unassigned</option>{confirmedPeople.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
                                                        <button onClick={()=>removeRepairItem(item.id)}><X className="w-3 h-3 text-slate-300"/></button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-bold uppercase text-slate-400 mb-2">Group First Aid Assignments</h4>
                                                {confirmedPeople.map(p => (
                                                    <div key={p.id} className="flex justify-between text-sm mb-1">
                                                        <span>{p.name}</span>
                                                        <div className="flex gap-2">
                                                            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={p.fa_personalKit || false} onChange={()=>updatePerson(p.id, 'fa_personalKit', !p.fa_personalKit)}/> Pers</label>
                                                            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={p.fa_groupKit || false} onChange={()=>updatePerson(p.id, 'fa_groupKit', !p.fa_groupKit)}/> Grp</label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'group_menu' && (
                            <div className="space-y-6">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-2 border-b border-pink-100">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-pink-50 rounded-lg text-pink-600"><Utensils className="w-6 h-6" /></div>
                                        <h2 className="text-2xl font-bold text-slate-800">Group Menu & Inventory</h2>
                                    </div>
                                    <div className="flex items-center gap-4 mt-4 md:mt-0">
                                        {groupMenuMode === 'plan' && (
                                            <button
                                                onClick={() => setIsGroupMenuEditing(!isGroupMenuEditing)}
                                                className={`px-4 py-2 rounded-lg text-sm font-bold border transition-all flex items-center gap-2 ${isGroupMenuEditing ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-pink-600 border-pink-200'}`}
                                            >
                                                {isGroupMenuEditing ? <><SaveAll className="w-4 h-4"/> Save Menu</> : <><Edit2 className="w-4 h-4"/> Edit Menu</>}
                                            </button>
                                        )}
                                        <div className="flex bg-slate-100 p-1 rounded-lg">
                                                {['checklist', 'plan', 'shopping'].map(m => (
                                                    <button key={m} onClick={() => setGroupMenuMode(m)} className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${groupMenuMode === m ? 'bg-white shadow-sm text-pink-700' : 'text-slate-500'}`}>{m}</button>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {groupMenuMode === 'checklist' && (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                                <div className="p-4 bg-indigo-50/50 border-b border-indigo-100 flex justify-between items-center">
                                                    <div className="flex items-center gap-3">
                                                        <ShoppingBag className="w-5 h-5 text-indigo-600"/>
                                                        <h3 className="text-lg font-bold text-slate-800">Group Food Inventory</h3>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="text-sm font-bold text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200 flex items-center gap-1" title="Packed Weight">
                                                            <CheckCircle2 className="w-3 h-3"/> {safeFixed(calculatePackedGroupFoodWeight(), 1)} lbs
                                                        </div>
                                                        <div className="text-sm font-medium text-indigo-800 bg-white px-2 py-1 rounded border border-indigo-200" title="Total Weight">
                                                            / {safeFixed(calculateGroupFoodWeight(), 1)} lbs
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                        {Object.keys(standardGroupFood).map(category => {
                                                            const categoryItems = groupFood.filter(i => i.category === category);
                                                            const categoryWeight = categoryItems.reduce((acc, i) => acc + (Number(i.weight) || 0), 0);
                                                           
                                                            return (
                                                                <div key={category} className="bg-slate-50/50 border border-slate-100 rounded-xl p-4">
                                                                    <div className="flex justify-between items-center border-b border-slate-100 pb-2 mb-3">
                                                                        <div className="flex items-center gap-2">
                                                                            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">{category}</h4>
                                                                            <span className="text-[10px] font-bold text-slate-300 bg-white px-1.5 py-0.5 rounded border">{safeFixed(categoryWeight, 1)} lb</span>
                                                                        </div>
                                                                        <button onClick={() => addStandardGroupFood(category)} className="text-[10px] text-indigo-600 hover:text-indigo-800 font-bold">+ Standard Kit</button>
                                                                    </div>
                                                                    <ul className="space-y-2">
                                                                        {categoryItems.map(item => (
                                                                            <li key={item.id} className="flex items-center gap-2 group">
                                                                                <button onClick={() => updateGroupFoodItem(item.id, 'packed', !item.packed)} className={`transition-colors ${item.packed ? 'text-emerald-500' : 'text-slate-300 hover:text-slate-400'}`}>
                                                                                    {item.packed ? <CheckCircle2 className="w-5 h-5"/> : <Circle className="w-5 h-5"/>}
                                                                                </button>
                                                                                <span className={`flex-grow text-sm transition-all ${item.packed ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>{item.item}</span>
                                                                                <input
                                                                                    type="number"
                                                                                    step="0.1"
                                                                                    value={item.weight || 0}
                                                                                    onChange={(e) => updateGroupFoodItem(item.id, 'weight', e.target.value)}
                                                                                    className={`w-12 text-right text-xs p-1 rounded border-none focus:ring-1 focus:ring-indigo-400 ${item.packed ? 'bg-emerald-50 text-emerald-700 font-bold' : 'bg-white'}`}
                                                                                    placeholder="0"
                                                                                />
                                                                                <button onClick={() => removeGroupFoodItem(item.id)} className="text-slate-200 hover:text-red-400 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5"/></button>
                                                                            </li>
                                                                        ))}
                                                                        <div className="flex gap-2 pt-1 border-t border-slate-100 mt-2">
                                                                            {addingToGroupFoodCategory === category ? (
                                                                                <div className="flex w-full gap-2 animate-in slide-in-from-top-1 duration-200">
                                                                                    <input autoFocus value={newGroupFoodName} onChange={e => setNewGroupFoodName(e.target.value)} className="text-xs border border-indigo-200 rounded px-2 py-1 flex-grow outline-none focus:ring-1 focus:ring-indigo-500" placeholder="New Item..." onKeyDown={e => e.key === 'Enter' && addGroupFoodItem(category)} />
                                                                                    <button onClick={() => addGroupFoodItem(category)} className="text-indigo-600"><Plus className="w-4 h-4"/></button>
                                                                                </div>
                                                                            ) : (
                                                                                <button onClick={() => setAddingToGroupFoodCategory(category)} className="text-[10px] font-bold text-slate-400 hover:text-indigo-600 uppercase flex items-center gap-1">+ Add Custom</button>
                                                                            )}
                                                                        </div>
                                                                    </ul>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                    </div>
                                )}
                               
                                {groupMenuMode === 'plan' && (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                            <div className="bg-white rounded-2xl border border-pink-100 p-6 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6">
                                                <div className="flex items-center gap-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-3 bg-pink-100 text-pink-700 rounded-xl"><Clock className="w-6 h-6"/></div>
                                                        <div>
                                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Trip Duration</label>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <input type="number" value={tripDuration} onChange={e=>updateDuration(e.target.value)} className="w-12 text-xl font-black text-pink-700 bg-transparent outline-none"/>
                                                                <span className="text-sm font-bold text-slate-500 uppercase">Days</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="h-10 w-px bg-slate-100"></div>
                                                    <div>
                                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Food Load</label>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className="text-xl font-black text-slate-800">{safeFixed(calculateTotalPlanWeight(), 1)}</span>
                                                            <span className="text-sm font-bold text-slate-500">lbs</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-center gap-3">
                                                    <Info className="w-5 h-5 text-amber-600"/>
                                                    <div className="text-xs text-amber-800 font-medium">
                                                        Items must be <strong>Packed</strong> in Inventory to appear in menu selectors.
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                {Array.from({length: tripDuration}, (_, i) => i + 1).map(day => {
                                                    const dKey = `day_${day}`;
                                                    const dData = menu[dKey] || {};
                                                   
                                                    return (
                                                        <div key={day} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col md:flex-row">
                                                            <div className="bg-slate-900 md:w-20 flex flex-col items-center justify-center py-4 px-2 text-white flex-shrink-0">
                                                                <span className="text-[10px] font-black opacity-50 uppercase tracking-widest">Day</span>
                                                                <span className="text-2xl font-black">{day}</span>
                                                            </div>
                                                           
                                                            <div className="flex-grow grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-0 divide-x divide-slate-50">
                                                                {['Breakfast', 'Lunch', 'Dinner', 'Dessert', 'Staples'].map(m => {
                                                                    const mKey = m.toLowerCase();
                                                                    const assignedItems = dData[`${mKey}_items`] || [];
                                                                    const mealText = dData[`${mKey}_meal`] || '';
                                                                    const chefId = dData[`${mKey}_chef`];

                                                                    const allowedCategories = [];
                                                                    if (mKey === 'breakfast') allowedCategories.push('Breakfast', 'Staples/Condiments');
                                                                    else if (mKey === 'lunch') allowedCategories.push('Lunch', 'Staples/Condiments');
                                                                    else if (mKey === 'dinner') allowedCategories.push('Dinner', 'Staples/Condiments');
                                                                    else if (mKey === 'dessert') allowedCategories.push('Dessert', 'Staples/Condiments');
                                                                    else if (mKey === 'staples') allowedCategories.push('Staples/Condiments');
                                                                   
                                                                    const relevantItems = groupFood.filter(i => allowedCategories.includes(i.category) && i.packed);

                                                                    return (
                                                                        <div key={m} className={`p-4 ${mKey === 'dinner' ? 'bg-pink-50/30' : ''}`}>
                                                                            <div className="flex justify-between items-center mb-2">
                                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{m}</span>
                                                                                {isGroupMenuEditing ? (
                                                                                    <select
                                                                                        className="text-[9px] border rounded bg-white p-0.5 max-w-[70px] outline-none"
                                                                                        value={chefId || ''}
                                                                                        onChange={e => updateMenu(day, mKey, 'chef', e.target.value)}
                                                                                    >
                                                                                        <option value="">Chef?</option>
                                                                                        {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                                                    </select>
                                                                                ) : (
                                                                                    chefId && <span className="text-[9px] font-bold text-pink-600 uppercase bg-pink-100 px-1.5 py-0.5 rounded">{getPersonName(chefId)}</span>
                                                                                )}
                                                                            </div>

                                                                            {isGroupMenuEditing ? (
                                                                                <div className="space-y-2">
                                                                                    <textarea
                                                                                        className="w-full border border-slate-100 rounded-lg p-2 text-xs h-16 resize-none focus:ring-1 focus:ring-pink-300 outline-none"
                                                                                        placeholder={`Plan ${m}...`}
                                                                                        value={mealText}
                                                                                        onChange={e => setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'menu', dKey), { [`${mKey}_meal`]: e.target.value }, { merge: true })}
                                                                                    />
                                                                                    <div className="space-y-1">
                                                                                        {assignedItems.map((itemId, idx) => {
                                                                                            const item = groupFood.find(gf => gf.id === itemId);
                                                                                            return item ? (
                                                                                                <div key={idx} className="flex justify-between items-center bg-white border border-slate-100 px-2 py-1 rounded text-[10px]">
                                                                                                    <span className="truncate">{item.item}</span>
                                                                                                    <button onClick={() => removeGroupDailyItem(day, mKey, itemId)} className="text-slate-300 hover:text-red-500"><X className="w-3 h-3"/></button>
                                                                                                </div>
                                                                                            ) : null;
                                                                                        })}
                                                                                        <select
                                                                                            className="w-full text-[10px] border border-dashed border-slate-200 rounded p-1 bg-transparent hover:bg-slate-50 cursor-pointer"
                                                                                            onChange={(e) => {
                                                                                                if(e.target.value) {
                                                                                                    addGroupDailyItem(day, mKey, e.target.value);
                                                                                                    e.target.value = "";
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            <option value="">+ Add Inv Item</option>
                                                                                            {relevantItems.map(item => (
                                                                                                <option key={item.id} value={item.id}>{item.item} ({item.weight} lb)</option>
                                                                                            ))}
                                                                                        </select>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="space-y-2">
                                                                                    <div className={`text-sm font-medium leading-relaxed ${!mealText ? 'text-slate-300 italic' : 'text-slate-700'}`}>
                                                                                        {mealText || 'No plan set'}
                                                                                    </div>
                                                                                    <div className="flex flex-wrap gap-1">
                                                                                        {assignedItems.map((itemId, idx) => {
                                                                                            const item = groupFood.find(gf => gf.id === itemId);
                                                                                            return item ? (
                                                                                                <span key={idx} className="text-[9px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                                                                                    {item.item}
                                                                                                </span>
                                                                                            ) : null;
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                    </div>
                                )}
                               
                                {groupMenuMode === 'shopping' && (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                            <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
                                                <div className="relative z-10">
                                                    <h3 className="text-2xl font-black flex items-center gap-3 mb-2 uppercase tracking-tight"><Calculator className="w-8 h-8"/> Menu Coverage</h3>
                                                    <p className="opacity-90 text-sm max-w-sm">Gap analysis between your 15-day menu plan and current physical inventory.</p>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 relative z-10">
                                                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 text-center border border-white/20">
                                                        <div className="text-3xl font-black">{Object.keys(shoppingAggregates.mealFrequency).length}</div>
                                                        <div className="text-[10px] font-black uppercase opacity-60">Distinct Meals</div>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 text-center border border-white/20">
                                                        <div className="text-3xl font-black">{Object.keys(shoppingAggregates.itemUsage).length}</div>
                                                        <div className="text-[10px] font-black uppercase opacity-60">Inventory Usage</div>
                                                    </div>
                                                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 text-center border border-white/20">
                                                        <div className="text-3xl font-black">{people.length * tripDuration * 3}</div>
                                                        <div className="text-[10px] font-black uppercase opacity-60">Servings</div>
                                                    </div>
                                                    <div className="bg-emerald-500 rounded-2xl p-4 text-center shadow-lg border border-emerald-400">
                                                        <div className="text-3xl font-black">{Math.round((Object.keys(shoppingAggregates.itemUsage).length / groupFood.length) * 100 || 0)}%</div>
                                                        <div className="text-[10px] font-black uppercase">Stock Match</div>
                                                    </div>
                                                </div>
                                                <div className="absolute -top-10 -right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-4 bg-slate-50 border-b border-slate-200">
                                                        <h4 className="font-black text-slate-800 text-xs uppercase tracking-widest flex items-center gap-2"><Utensils className="w-4 h-4 text-pink-500"/> Plan Frequency Analysis</h4>
                                                    </div>
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="bg-slate-50 text-slate-400 text-[10px] uppercase font-black">
                                                            <tr>
                                                                <th className="p-4">Meal Description</th>
                                                                <th className="p-4 text-center">Freq</th>
                                                                <th className="p-4 text-right">Days</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {Object.values(shoppingAggregates.mealFrequency).sort((a,b) => b.count - a.count).map((item, idx) => (
                                                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                                                    <td className="p-4 font-bold text-slate-700">{item.name}</td>
                                                                    <td className="p-4 text-center"><span className="bg-pink-50 text-pink-700 px-2 py-1 rounded-lg border border-pink-100 text-xs font-black">{item.count}x</span></td>
                                                                    <td className="p-4 text-right text-[10px] text-slate-400 font-bold uppercase">{item.examples.join(', ')}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                                    <div className="p-4 bg-slate-50 border-b border-slate-200">
                                                        <h4 className="font-black text-slate-800 text-xs uppercase tracking-widest flex items-center gap-2"><PackageCheck className="w-4 h-4 text-emerald-500"/> Inventory Load Distribution</h4>
                                                    </div>
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="bg-slate-50 text-slate-400 text-[10px] uppercase font-black">
                                                            <tr>
                                                                <th className="p-4">Item</th>
                                                                <th className="p-4 text-center">Times Planned</th>
                                                                <th className="p-4 text-right">Unit Weight</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {Object.entries(shoppingAggregates.itemUsage).map(([id, count]) => {
                                                                const item = groupFood.find(i => i.id === id);
                                                                return item ? (
                                                                    <tr key={id} className="hover:bg-slate-50 transition-colors">
                                                                        <td className="p-4 flex items-center gap-3">
                                                                            <div className={`w-2 h-2 rounded-full ${item.packed ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-400'}`}></div>
                                                                            <span className="font-bold text-slate-700">{item.item}</span>
                                                                        </td>
                                                                        <td className="p-4 text-center"><span className="font-black text-slate-800">{count}</span></td>
                                                                        <td className="p-4 text-right font-mono text-xs text-slate-500">{item.weight} lb</td>
                                                                    </tr>
                                                                ) : null;
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'personal_menu' && (
                            <div className="space-y-6">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-2 border-b border-teal-100">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-teal-50 rounded-lg text-teal-600"><Coffee className="w-6 h-6" /></div>
                                        <h2 className="text-2xl font-bold text-slate-800">Personal Menu</h2>
                                    </div>
                                    <div className="flex items-center gap-4 mt-4 md:mt-0">
                                        <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1 shadow-sm">
                                            <Filter className="w-3.5 h-3.5 text-slate-400"/>
                                            <select
                                                value={viewingAs}
                                                onChange={e => setViewingAs(e.target.value)}
                                                className="text-sm font-bold text-slate-600 outline-none bg-transparent"
                                            >
                                                <option value="">Show All Crew</option>
                                                {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="flex bg-slate-100 p-1 rounded-lg">
                                                {['checklist', 'daily'].map(m => (
                                                    <button key={m} onClick={() => setPersonalMenuMode(m)} className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${personalMenuMode === m ? 'bg-white shadow-sm text-teal-700' : 'text-slate-500'}`}>{m}</button>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {personalMenuMode === 'checklist' && (
                                    <div className="space-y-6">
                                            {confirmedPeople.filter(p => !viewingAs || p.id === viewingAs).map(p => {
                                                const list = personalMenus[p.id];
                                                const totalWeight = calculatePersonalMenuChecklistWeight(p.id);
                                                const expandedKey = `menu_check_${p.id}`;
                                               
                                                const items = list?.items || [];
                                                const packedCount = items.filter(i => i.packed).length;
                                                const totalCount = items.length;

                                                return (
                                                    <div key={p.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                                        <div className="p-4 bg-teal-50/50 border-b border-teal-100 flex justify-between items-center cursor-pointer" onClick={() => toggleSection(expandedKey)}>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-slate-500">{isSectionExpanded(expandedKey) ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</span>
                                                                    <h3 className="font-bold text-slate-800">{p.name}'s Checklist</h3>
                                                                    <span className="text-xs font-bold text-teal-700 bg-white px-2 py-0.5 rounded-full border border-teal-200">{safeFixed(totalWeight, 1)} lbs</span>
                                                                </div>
                                                                <div className="mt-2 w-full max-w-xs">
                                                                    <ProgressBar value={packedCount} max={totalCount} colorClass="bg-teal-500" />
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {(!list || !list.items) && <button onClick={(e) => {e.stopPropagation(); generatePersonalMenu(p.id)}} className="text-xs bg-teal-600 text-white px-3 py-1.5 rounded-lg font-bold shadow-sm">Load Standard Menu</button>}
                                                            </div>
                                                        </div>
                                                        {isSectionExpanded(expandedKey) && list && list.items && (
                                                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                                {Object.keys(standardPersonalMenu).map(cat => (
                                                                    <div key={cat}>
                                                                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-2 border-b border-slate-100 pb-1">{cat}</h4>
                                                                        <div className="space-y-1">
                                                                            {list.items.filter(i => i.category === cat).map(i => (
                                                                                <div key={i.id} className="flex items-center gap-2 group text-sm">
                                                                                    <button onClick={() => updatePersonalMenuItem(p.id, i.id, 'packed', !i.packed)}>{i.packed ? <CheckCircle2 className="w-4 h-4 text-teal-500"/> : <Circle className="w-4 h-4 text-slate-300"/>}</button>
                                                                                    <span className={`flex-grow transition-colors duration-200 ${i.packed ? 'text-green-700 font-bold' : 'text-slate-700'}`}>{i.name}</span>
                                                                                    <input type="number" step="0.1" value={i.weight||0} onChange={e=>updatePersonalMenuItem(p.id, i.id, 'weight', e.target.value)} className={`w-12 text-right text-xs p-1 rounded border-none focus:ring-1 focus:ring-teal-400 ${i.packed ? 'bg-green-50 text-green-700 font-bold' : 'bg-slate-50'}`} placeholder="lbs"/>
                                                                                    <button onClick={()=>removePersonalMenuItem(p.id, i.id)} className="text-slate-200 hover:text-red-400 opacity-0 group-hover:opacity-100"><X className="w-3 h-3"/></button>
                                                                                </div>
                                                                            ))}
                                                                            <div className="flex gap-2 pt-1">
                                                                                {addingToMenuCategory.personId === p.id && addingToMenuCategory.category === cat ? (
                                                                                    <><input autoFocus value={newMenuItemName} onChange={e=>setNewMenuItemName(e.target.value)} onKeyDown={e=>e.key==='Enter' && addCustomPersonalMenuItem()} className="text-xs border rounded p-1 flex-grow outline-none focus:ring-1 focus:ring-teal-400" placeholder="New Item..."/><button onClick={addCustomPersonalMenuItem} className="text-teal-600"><Plus className="w-4 h-4"/></button></>
                                                                                ) : (
                                                                                    <button onClick={() => setAddingToMenuCategory({personId: p.id, category: cat})} className="text-[10px] font-bold text-slate-400 hover:text-teal-600 uppercase tracking-wider flex items-center gap-1">+ Add Custom</button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}

                                {personalMenuMode === 'daily' && (
                                    <div className="space-y-6">
                                            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl border border-teal-100">
                                                <div className="flex items-center gap-4">
                                                    <label className="font-bold text-slate-700">Trip Duration:</label>
                                                    <input type="number" value={tripDuration} onChange={e=>updateDuration(e.target.value)} className="w-16 border rounded p-1 text-center focus:ring-teal-200 focus:border-teal-300 outline-none"/>
                                                    <span className="text-sm text-slate-500">Days</span>
                                                </div>
                                                <div className="flex items-center gap-2 bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-200">
                                                    <Scale className="w-4 h-4 text-teal-600"/>
                                                    <span className="text-sm font-bold text-slate-600 uppercase">Total Personal Food:</span>
                                                    <span className="text-lg font-black text-teal-700">{safeFixed(totalPersonalMenuWeight, 1)} lbs</span>
                                                </div>
                                            </div>

                                            {confirmedPeople.filter(p => !viewingAs || p.id === viewingAs).map(person => {
                                                const myItems = personalMenus[person.id]?.items || [];
                                                const dailyData = personalMenus[person.id]?.daily || {};
                                                const expandedKey = `menu_daily_${person.id}`;
                                                const myItemMap = new Map(myItems.map(i => [i.id, i]));
                                                const planTotal = calculatePersonalMenuPlanWeight(person.id);
                                                const checklistTotal = calculatePersonalMenuChecklistWeight(person.id);

                                                return (
                                                    <div key={person.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                                                        <div className="p-4 bg-teal-50/50 border-b border-teal-100 flex justify-between items-center cursor-pointer" onClick={() => toggleSection(expandedKey)}>
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-slate-500">{isSectionExpanded(expandedKey) ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</span>
                                                                <h3 className="font-bold text-slate-800">{person.name}'s Daily Plan</h3>
                                                            </div>
                                                            <div className="flex items-center gap-3 text-sm">
                                                                <div className="flex flex-col items-end">
                                                                    <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Plan Total</span>
                                                                    <span className="font-bold text-teal-800">{safeFixed(planTotal, 1)} lbs</span>
                                                                </div>
                                                                <div className="h-8 w-px bg-teal-200"></div>
                                                                <div className="flex flex-col items-end">
                                                                    <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Checklist</span>
                                                                    <span className="font-bold text-slate-600">{safeFixed(checklistTotal, 1)} lbs</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {isSectionExpanded(expandedKey) && (
                                                            <div className="p-4 grid grid-cols-1 gap-4">
                                                                {Array.from({ length: tripDuration }, (_, i) => i + 1).map(day => {
                                                                    const dayData = dailyData[`day_${day}`] || {};
                                                                    return (
                                                                        <div key={day} className="p-0 border border-teal-100 rounded-xl bg-white shadow-sm flex flex-col md:flex-row overflow-hidden">
                                                                            <div className="bg-slate-50 p-4 w-full md:w-24 flex flex-col justify-center items-center border-b md:border-b-0 md:border-r border-slate-100 flex-shrink-0 font-black text-slate-400">DAY {day}</div>
                                                                            <div className="flex-grow p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                                                                {['Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Drinks', 'Desserts'].map(meal => {
                                                                                    const mKey = meal.toLowerCase();
                                                                                    const selectedItemIds = dayData[`${mKey}_items`] || [];
                                                                                    const allowedCategories = [];
                                                                                    if (mKey === 'breakfast') allowedCategories.push('Breakfast');
                                                                                    else if (mKey === 'lunch') allowedCategories.push('Lunch');
                                                                                    else if (mKey === 'dinner') allowedCategories.push('Dinner');
                                                                                    else if (mKey === 'snacks') allowedCategories.push('Snacks');
                                                                                    else if (mKey === 'drinks') allowedCategories.push('Drinks');
                                                                                    else if (mKey === 'desserts') allowedCategories.push('Dessert');
                                                                                   
                                                                                    const relevantItems = myItems.filter(i => allowedCategories.includes(i.category) && i.packed);

                                                                                    return (
                                                                                        <div key={meal} className="space-y-1">
                                                                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">{meal}</label>
                                                                                            {selectedItemIds.length > 0 && (
                                                                                                <div className="space-y-1 mb-1">
                                                                                                    {selectedItemIds.map((itemId, idx) => {
                                                                                                        const item = myItemMap.get(itemId);
                                                                                                        if (!item) return null;
                                                                                                        return (
                                                                                                            <div key={`${itemId}-${idx}`} className="flex justify-between items-center bg-teal-50 border border-teal-100 rounded px-2 py-0.5 text-[10px]">
                                                                                                                <span className="truncate mr-1 font-medium">{item.name}</span>
                                                                                                                <button onClick={() => removePersonalDailyItem(person.id, day, mKey, itemId)} className="text-teal-300 hover:text-red-500"><X className="w-3 h-3" /></button>
                                                                                                            </div>
                                                                                                        );
                                                                                                    })}
                                                                                                </div>
                                                                                            )}
                                                                                            <select
                                                                                                className="w-full p-1 text-[10px] border border-slate-100 rounded text-slate-500 outline-none hover:bg-slate-50 transition-colors"
                                                                                                onChange={(e) => {
                                                                                                    if (e.target.value) {
                                                                                                        addPersonalDailyItem(person.id, day, mKey, e.target.value);
                                                                                                        e.target.value = "";
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                <option value="">+ Add...</option>
                                                                                                {relevantItems.map(item => (
                                                                                                    <option key={item.id} value={item.id}>{item.name} ({item.weight} lbs)</option>
                                                                                                ))}
                                                                                            </select>
                                                                                            <input
                                                                                                className="w-full border-b border-transparent text-[10px] focus:border-teal-400 outline-none pb-0.5 italic text-slate-500 bg-transparent"
                                                                                                placeholder="Notes..."
                                                                                                value={dayData[mKey] || ''}
                                                                                                onChange={e => updatePersonalDailyMenu(person.id, day, mKey, e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'personal' && (
                            <div className="space-y-6">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-2 border-b border-green-100">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-green-50 rounded-lg text-green-600"><Backpack className="w-6 h-6" /></div>
                                        <h2 className="text-2xl font-bold text-slate-800">Personal Gear Manifests</h2>
                                    </div>
                                    <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1 shadow-sm mt-4 md:mt-0">
                                        <Filter className="w-3.5 h-3.5 text-slate-400"/>
                                        <select
                                            value={viewingAs}
                                            onChange={e => setViewingAs(e.target.value)}
                                            className="text-sm font-bold text-slate-600 outline-none bg-transparent"
                                        >
                                            <option value="">Show All Crew</option>
                                            {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {confirmedPeople.filter(p => !viewingAs || p.id === viewingAs).map(p => {
                                    const list = personalLists[p.id];
                                    const totalWeight = calculatePersonalWeight(p.id);
                                    const expandedKey = `gear_${p.id}`;
                                   
                                    const items = list?.items || [];
                                    const packedCount = items.filter(i => i.packed).length;
                                    const totalCount = items.length;

                                    return (
                                        <div key={p.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-4">
                                            <div className="p-4 bg-green-50/50 border-b border-green-100 flex justify-between items-center cursor-pointer" onClick={() => toggleSection(expandedKey)}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-slate-500">{isSectionExpanded(expandedKey) ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}</span>
                                                        <h3 className="font-bold text-slate-800">{p.name}'s Pack</h3>
                                                        <span className="text-xs font-bold text-green-700 bg-white px-2 py-0.5 rounded-full border border-teal-200">{safeFixed(totalWeight, 1)} lbs</span>
                                                    </div>
                                                    <div className="mt-2 w-full max-w-xs">
                                                        <ProgressBar value={packedCount} max={totalCount} colorClass="bg-green-500" />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    {!list && <button onClick={(e) => { e.stopPropagation(); generatePersonalList(p.id); }} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold shadow-md hover:bg-green-700 transition-colors">Load Standard List</button>}
                                                </div>
                                            </div>
                                            {isSectionExpanded(expandedKey) && list && (
                                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {Object.entries(standardPersonalKit).map(([category, items]) => (
                                                        <div key={category} className="space-y-2">
                                                            <div className="flex justify-between items-center border-b border-slate-100 pb-1">
                                                                <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{category}</h4>
                                                                <button onClick={() => setAddingToCategory({ personId: p.id, category })} className="text-xs text-green-600 hover:text-green-800 transition-colors"><Plus className="w-4 h-4"/></button>
                                                            </div>
                                                            <ul className="space-y-1.5">
                                                                {list.items.filter(i => i.category === category).map(item => (
                                                                    <li key={item.id} className="flex items-center gap-2 group">
                                                                        <button onClick={() => updatePersonalItem(p.id, item.id, 'packed', !item.packed)} className={`transition-colors ${item.packed ? 'text-green-500' : 'text-slate-300 hover:text-slate-400'}`}>
                                                                            {item.packed ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                                                                        </button>
                                                                        <span className={`text-sm flex-grow transition-all duration-300 ${item.packed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                                                            {item.name}
                                                                        </span>
                                                                        <input
                                                                            type="number"
                                                                            step="0.1"
                                                                            value={item.weight || 0}
                                                                            onChange={(e) => updatePersonalItem(p.id, item.id, 'weight', e.target.value)}
                                                                            className={`w-12 text-right text-xs bg-slate-50 border-none rounded p-1 transition-all focus:ring-1 focus:ring-green-400 ${item.packed ? 'opacity-50' : ''}`}
                                                                        />
                                                                        <button onClick={() => removePersonalItem(p.id, item.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3.5 h-3.5"/></button>
                                                                    </li>
                                                                ))}
                                                                {addingToCategory.personId === p.id && addingToCategory.category === category && (
                                                                    <div className="flex gap-2 mt-2 animate-in slide-in-from-top-2 duration-200">
                                                                        <input
                                                                            autoFocus
                                                                            value={newItemName}
                                                                            onChange={e => setNewItemName(e.target.value)}
                                                                            onKeyDown={e => e.key === 'Enter' && addCustomItem()}
                                                                            className="flex-grow text-xs border border-green-200 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-green-400"
                                                                            placeholder="Item name..."
                                                                        />
                                                                        <button onClick={addCustomItem} className="p-1 bg-green-600 text-white rounded shadow-sm hover:bg-green-700 transition-colors"><Plus className="w-4 h-4"/></button>
                                                                        <button onClick={() => setAddingToCategory({personId: null, category: null})} className="p-1 bg-slate-100 text-slate-400 rounded"><X className="w-4 h-4"/></button>
                                                                    </div>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {activeTab === 'load_weights' && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <SectionHeader title="Expedition Loads & Weights" icon={Scale} color="rose" />
                               
                                <div className="bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl p-6 text-white shadow-lg mb-6">
                                    <h3 className="text-lg font-bold mb-2 opacity-90">Total Expedition Weight</h3>
                                    <div className="text-4xl font-black">{safeFixed(totalExpeditionWeight, 1)} <span className="text-xl font-normal opacity-70">lbs</span></div>
                                    <p className="text-xs mt-2 opacity-75">Includes canoes (hull + skirts), group gear, personal packs, and estimated food.</p>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-6">
                                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                                        <PieChart className="w-5 h-5 text-indigo-500"/> Load Distribution Analysis
                                    </h3>
                                    <div className="h-10 w-full bg-slate-100 rounded-full flex overflow-hidden">
                                        <div style={{ width: `${(totalCanoeWeight / totalExpeditionWeight) * 100}%` }} className="h-full bg-blue-400" title="Canoes"></div>
                                        <div style={{ width: `${(totalGroupGearWeight / totalExpeditionWeight) * 100}%` }} className="h-full bg-orange-400" title="Group Gear"></div>
                                        <div style={{ width: `${(totalPersonalGearWeight / totalExpeditionWeight) * 100}%` }} className="h-full bg-green-400" title="Personal Gear"></div>
                                        <div style={{ width: `${(estimatedTotalFoodWeight / totalExpeditionWeight) * 100}%` }} className="h-full bg-pink-400" title="Food"></div>
                                    </div>
                                    <div className="flex flex-wrap gap-4 mt-4 text-xs font-bold text-slate-600 justify-center">
                                        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-400 rounded-full"></div> Canoes ({safeFixed((totalCanoeWeight / totalExpeditionWeight) * 100, 0)}%)</div>
                                        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-orange-400 rounded-full"></div> Group Gear ({safeFixed((totalGroupGearWeight / totalExpeditionWeight) * 100, 0)}%)</div>
                                        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-400 rounded-full"></div> Pers. Gear ({safeFixed((totalPersonalGearWeight / totalExpeditionWeight) * 100, 0)}%)</div>
                                        <div className="flex items-center gap-2"><div className="w-3 h-3 bg-pink-400 rounded-full"></div> Food ({safeFixed((estimatedTotalFoodWeight / totalExpeditionWeight) * 100, 0)}%)</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                        <div className="text-xs font-bold text-slate-400 uppercase">Canoe Fleet</div>
                                        <div className="text-2xl font-bold text-slate-800">{totalCanoeWeight} lbs</div>
                                        <div className="text-xs text-slate-500">{canoes.length} boats (w/ skirts)</div>
                                    </div>
                                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                        <div className="text-xs font-bold text-slate-400 uppercase">Group Gear Manifest</div>
                                        <div className="text-2xl font-bold text-slate-800">{safeFixed(totalGearManifestWeight, 1)} lbs</div>
                                        <div className="text-xs text-slate-500">Equipment only (no food)</div>
                                    </div>
                                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                        <div className="text-xs font-bold text-slate-400 uppercase">Avg Personal Pack</div>
                                        <div className="text-2xl font-bold text-slate-800">{safeFixed(avgPersonalGearWeight, 1)} lbs</div>
                                        <div className="text-xs text-slate-500">Per person average</div>
                                    </div>
                                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                                        <div className="text-xs font-bold text-slate-400 uppercase">Est. Food Weight</div>
                                        <div className="text-2xl font-bold text-slate-800">{safeFixed(estimatedTotalFoodWeight, 0)} lbs</div>
                                        <div className="text-xs text-slate-500">{foodLbsPerDay} lbs/day/person</div>
                                    </div>
                                </div>
                            </div>
                        )}



                        {activeTab === 'expenses' && (
    <div className="space-y-6">
        <SectionHeader
            title="Expenses & Settlement"
            icon={DollarSign}
            color="emerald"
            action={
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={exportExpensesToCSV}
                        className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-50 flex items-center gap-2"
                    >
                        <Download className="w-4 h-4"/> CSV
                    </button>
                    <button
                        onClick={generateExpenseReport}
                        className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-50 flex items-center gap-2"
                    >
                        <Printer className="w-4 h-4"/> Report
                    </button>
                    <button
                        onClick={copyExpenseSummaryToClipboard}
                        className="bg-white border border-slate-300 text-slate-700 px-3 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-50 flex items-center gap-2"
                    >
                        <Clipboard className="w-4 h-4"/> Copy Summary
                    </button>
                    <button
                        onClick={() => setIsSplitMode(!isSplitMode)}
                        className={`px-4 py-2 rounded-lg text-sm font-bold shadow-sm flex items-center gap-2 border ${isSplitMode ? 'bg-slate-100 text-slate-600 border-slate-300' : 'bg-white text-emerald-600 border-emerald-600'}`}
                    >
                        {isSplitMode ? <X className="w-4 h-4"/> : <Split className="w-4 h-4"/>} {isSplitMode ? 'Simple' : 'Split?'}
                    </button>
                    <button
                        onClick={() => setIsSettlementModalOpen(true)}
                        className="bg-white border border-emerald-600 text-emerald-600 px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-emerald-50 flex items-center gap-2"
                    >
                        <ArrowLeftRight className="w-4 h-4"/> Settle Up
                    </button>
                    <button
                        onClick={addExpense}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-emerald-700 flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4"/> Add Expense
                    </button>
                </div>
            }
        />

        {/* Per-Person Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400"/>
                    <label className="text-sm font-bold text-slate-600">Filter View:</label>
                </div>
                <select
                    value={viewingAs}
                    onChange={e => setViewingAs(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm font-medium"
                >
                    <option value="">All Expenses</option>
                    {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {viewingAs && (
                    <>
                        <div className="h-6 w-px bg-slate-200"></div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setExpenseViewFilter('all')}
                                className={`px-3 py-1 rounded text-xs font-bold ${expenseViewFilter === 'all' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setExpenseViewFilter('paid')}
                                className={`px-3 py-1 rounded text-xs font-bold ${expenseViewFilter === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                            >
                                Paid By Me
                            </button>
                            <button
                                onClick={() => setExpenseViewFilter('owed')}
                                className={`px-3 py-1 rounded text-xs font-bold ${expenseViewFilter === 'owed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                            >
                                I Owe Share
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Per-Person Summary Card */}
            {viewingAs && (() => {
                const personBalance = expenseSummary?.balances.find(b => b.id === viewingAs);
                const personExpenses = expenses.filter(e => 
                    Object.keys(e.paidBy || {}).includes(viewingAs) ||
                    (e.splitWith || []).includes(viewingAs) ||
                    (!e.splitWith || e.splitWith.length === 0)
                );
                const paidExpenses = expenses.filter(e => Object.keys(e.paidBy || {}).includes(viewingAs));
                const owedExpenses = expenses.filter(e => {
                    const involved = e.splitWith || [];
                    return involved.includes(viewingAs) || (involved.length === 0 && !e.isSettlement);
                });

                return (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                        <h4 className="text-sm font-bold text-slate-700 mb-3">
                            {confirmedPeople.find(p => p.id === viewingAs)?.name}'s Summary
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-slate-50 rounded-lg p-3">
                                <div className="text-xs text-slate-500 uppercase font-bold">Total Paid</div>
                                <div className="text-xl font-black text-slate-800">
                                    ${safeFixed(personBalance?.paid || 0, 2)}
                                </div>
                                <div className="text-xs text-slate-400">{paidExpenses.length} expenses</div>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                                <div className="text-xs text-slate-500 uppercase font-bold">My Share</div>
                                <div className="text-xl font-black text-slate-800">
                                    ${safeFixed(personBalance?.share || 0, 2)}
                                </div>
                                <div className="text-xs text-slate-400">{owedExpenses.length} involved</div>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                                <div className="text-xs text-slate-500 uppercase font-bold">Balance</div>
                                <div className={`text-xl font-black ${(personBalance?.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {(personBalance?.balance || 0) >= 0 ? '+' : ''}${safeFixed(personBalance?.balance || 0, 2)}
                                </div>
                                <div className="text-xs text-slate-400">
                                    {(personBalance?.balance || 0) >= 0 ? 'Owed to me' : 'I owe'}
                                </div>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3">
                                <div className="text-xs text-slate-500 uppercase font-bold">Total Expenses</div>
                                <div className="text-xl font-black text-slate-800">{personExpenses.length}</div>
                                <div className="text-xs text-slate-400">Paid or involved</div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>

        {isSettlementModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md animate-in zoom-in duration-200">
                    <div className="flex justify-between items-center mb-4 border-b pb-2">
                        <h3 className="text-lg font-bold text-slate-800">Record Settlement</h3>
                        <button onClick={() => setIsSettlementModalOpen(false)}><X className="w-5 h-5 text-slate-400"/></button>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Payer (From)</label>
                            <select
                                value={settlementData.from}
                                onChange={e => setSettlementData({...settlementData, from: e.target.value})}
                                className="w-full border rounded-lg p-2"
                            >
                                <option value="">Select Payer...</option>
                                {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="flex justify-center text-slate-400"><ArrowRight className="w-6 h-6"/></div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Recipient (To)</label>
                            <select
                                value={settlementData.to}
                                onChange={e => setSettlementData({...settlementData, to: e.target.value})}
                                className="w-full border rounded-lg p-2"
                            >
                                <option value="">Select Recipient...</option>
                                {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Amount ($)</label>
                            <input
                                type="number"
                                value={settlementData.amount}
                                onChange={e => setSettlementData({...settlementData, amount: e.target.value})}
                                className="w-full border rounded-lg p-2 font-mono"
                                placeholder="0.00"
                            />
                        </div>
                        <button
                            onClick={recordSettlement}
                            disabled={!settlementData.from || !settlementData.to || !settlementData.amount || settlementData.from === settlementData.to}
                            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-bold shadow-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Confirm Transfer
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* Balance Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 bg-emerald-50 border-b border-emerald-100">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-emerald-600"/> Balance Summary
                </h3>
            </div>
            <div className="p-6">
                <div className="mb-6 pb-6 border-b border-slate-100">
                    <div className="text-sm text-slate-500 uppercase font-bold tracking-wider mb-1">Total Trip Cost</div>
                    <div className="text-3xl font-black text-slate-800">
                        ${safeFixed(expenseSummary?.totalTripCost || 0, 2)}
                    </div>
                </div>

                <div className="space-y-3 mb-6">
                    <div className="text-sm text-slate-500 uppercase font-bold tracking-wider mb-3">Individual Balances</div>
                    {expenseSummary && expenseSummary.balances.length > 0 ? (
                        expenseSummary.balances.map(b => (
                            <div key={b.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                                <span className="font-medium text-slate-700">{b.name}</span>
                                <span className={`font-mono font-bold text-lg ${b.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {b.balance >= 0 ? '+' : ''}{safeFixed(b.balance, 2)}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="text-center text-slate-400 italic py-4">No crew members confirmed yet</div>
                    )}
                </div>

                {settlements.length > 0 ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                        <div className="text-sm text-emerald-800 uppercase font-bold tracking-wider mb-3 flex items-center gap-2">
                            <ArrowLeftRight className="w-4 h-4"/> Settlement Plan
                        </div>
                        <div className="space-y-2">
                            {settlements.map((tx, i) => (
                                <div key={i} className="flex justify-between items-center p-2 bg-white rounded text-sm">
                                    <span>
                                        <span className="font-bold text-slate-800">{tx.from}</span>
                                        <ArrowRight className="w-3 h-3 inline mx-2 text-slate-400"/>
                                        <span className="font-bold text-slate-800">{tx.to}</span>
                                    </span>
                                    <span className="font-mono font-bold text-emerald-700">${safeFixed(tx.amount, 2)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2"/>
                        <div className="text-lg font-bold text-emerald-800">All Settled Up!</div>
                        <div className="text-sm text-emerald-600 mt-1">No outstanding balances</div>
                    </div>
                )}
            </div>
        </div>

        {/* Settlement History */}
        {(() => {
            const settlementHistory = expenses.filter(e => e.isSettlement).sort((a, b) => (b.settlementTimestamp || 0) - (a.settlementTimestamp || 0));
            return settlementHistory.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-slate-600"/> Settlement History
                        </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {settlementHistory.map(s => (
                            <div key={s.id} className="p-4 flex justify-between items-center hover:bg-slate-50">
                                <div>
                                    <div className="font-medium text-slate-800">{s.item}</div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        {s.settlementTimestamp && new Date(s.settlementTimestamp).toLocaleString()}
                                        {s.date && ` • Date: ${s.date}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="font-mono font-bold text-emerald-700">${safeFixed(s.totalCost, 2)}</span>
                                    <label className="flex items-center gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={s.settlementCompleted || false}
                                            onChange={e => updateExpense(s.id, 'settlementCompleted', e.target.checked)}
                                            className="w-4 h-4 rounded text-emerald-600"
                                        />
                                        <span className="text-slate-600">Completed</span>
                                    </label>
                                    <button
                                        onClick={() => undoSettlement(s.id)}
                                        className="text-slate-400 hover:text-red-500 transition-colors"
                                        title="Undo Settlement"
                                    >
                                        <X className="w-4 h-4"/>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null;
        })()}

        {/* Expenses Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-emerald-50 text-emerald-900 font-bold">
                        <tr>
                            <th className="p-3 w-10"></th>
                            <th className="p-3 w-24">Date</th>
                            <th className="p-3">Item</th>
                            <th className="p-3 w-24 text-right">Cost</th>
                            {isSplitMode ? (
                                confirmedPeople.map(p => <th key={p.id} className="p-3 w-20 text-center text-xs truncate">{p.name}</th>)
                            ) : (
                                <>
                                    <th className="p-3">Paid By</th>
                                    <th className="p-3 w-32">Payment Method</th>
                                </>
                            )}
                            <th className="p-3 w-20 text-center text-xs">Status</th>
                            <th className="p-3 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {expenses
                            .filter(e => {
                                if (!viewingAs) return true;
                                if (expenseViewFilter === 'paid') {
                                    return Object.keys(e.paidBy || {}).includes(viewingAs);
                                }
                                if (expenseViewFilter === 'owed') {
                                    const involved = e.splitWith || [];
                                    return involved.includes(viewingAs) || (involved.length === 0 && !e.isSettlement);
                                }
                                return Object.keys(e.paidBy || {}).includes(viewingAs) ||
                                       (e.splitWith || []).includes(viewingAs) ||
                                       (!e.splitWith || e.splitWith.length === 0);
                            })
                            .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                            .map(e => {
                                const paidSum = Object.values(e.paidBy || {}).reduce((a, b) => a + (parseFloat(b) || 0), 0);
                                const cost = parseFloat(e.totalCost) || 0;
                                const diff = paidSum - cost;
                                let statusContent;
                                
                                if (Math.abs(diff) < 0.02) statusContent = <span className="text-green-600 flex justify-center"><Check className="w-4 h-4"/></span>;
                                else if (diff < 0) statusContent = <span className="text-red-500 font-mono text-[10px] whitespace-nowrap">-${Math.abs(diff).toFixed(2)}</span>;
                                else statusContent = <span className="text-orange-500 font-mono text-[10px] whitespace-nowrap">+{diff.toFixed(2)}</span>;

                                const isExpanded = expandedExpense === e.id;

                                return (
                                    <React.Fragment key={e.id}>
                                        <tr className="hover:bg-slate-50">
                                            <td className="p-2 pl-3">
                                                <button
                                                    onClick={() => setExpandedExpense(isExpanded ? null : e.id)}
                                                    className="text-slate-400 hover:text-slate-600"
                                                >
                                                    {isExpanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                                                </button>
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="date"
                                                    value={e.date || ''}
                                                    onChange={ev => updateExpense(e.id, 'date', ev.target.value)}
                                                    className="bg-transparent w-full text-xs p-1 rounded border border-transparent hover:border-slate-200"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    value={e.item}
                                                    onChange={ev => updateExpense(e.id, 'item', ev.target.value)}
                                                    className={`bg-transparent w-full font-medium ${e.isSettlement ? 'text-slate-500 italic' : ''}`}
                                                    placeholder="Description..."
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="number"
                                                    value={e.totalCost}
                                                    onChange={ev => handleExpenseCostUpdate(e, ev.target.value)}
                                                    className="bg-transparent w-full text-right font-mono"
                                                    placeholder="0.00"
                                                />
                                            </td>
                                            
                                            {isSplitMode ? (
                                                confirmedPeople.map(p => (
                                                    <td key={p.id} className="p-2 text-center border-l border-slate-50">
                                                        <input
                                                            type="number"
                                                            value={e.paidBy?.[p.id] || ''}
                                                            onChange={ev => updateExpensePayment(e.id, p.id, ev.target.value)}
                                                            className="bg-transparent w-full text-center text-xs"
                                                            placeholder="-"
                                                        />
                                                        {!e.isSettlement && (
                                                            <div className="flex justify-center mt-1">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!e.splitWith || e.splitWith.length === 0 || e.splitWith.includes(p.id)}
                                                                    onChange={() => toggleExpenseSplit(e, p.id)}
                                                                    title="Split share?"
                                                                    className="w-3 h-3 accent-emerald-500"
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                ))
                                            ) : (
                                                <>
                                                    <td className="p-2 border-l border-slate-50">
                                                        {!e.isSettlement && (
                                                            <select
                                                                value={Object.keys(e.paidBy || {})[0] || ''}
                                                                onChange={(ev) => {
                                                                    const pid = ev.target.value;
                                                                    if (pid) updateExpense(e.id, 'paidBy', { [pid]: parseFloat(e.totalCost) || 0 });
                                                                    else updateExpense(e.id, 'paidBy', {});
                                                                }}
                                                                className="w-full bg-transparent text-sm border-none focus:ring-0"
                                                            >
                                                                <option value="">Select Payer...</option>
                                                                {confirmedPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                            </select>
                                                        )}
                                                        {e.isSettlement && <span className="text-xs text-slate-400 italic pl-2">Settlement</span>}
                                                    </td>
                                                    <td className="p-2 border-l border-slate-50">
                                                        <select
                                                            value={e.paymentMethod || ''}
                                                            onChange={ev => updateExpense(e.id, 'paymentMethod', ev.target.value)}
                                                            className="w-full bg-transparent text-xs border-none focus:ring-0"
                                                        >
                                                            <option value="">Method...</option>
                                                            <option value="Cash">Cash</option>
                                                            <option value="Card">Card</option>
                                                            <option value="E-transfer">E-transfer</option>
                                                            <option value="Venmo">Venmo</option>
                                                            <option value="PayPal">PayPal</option>
                                                            <option value="Check">Check</option>
                                                        </select>
                                                    </td>
                                                </>
                                            )}

                                            <td className="p-2 text-center border-l border-slate-50">{statusContent}</td>
                                            <td className="p-2 text-center">
                                                <button onClick={() => removeExpense(e.id)} className="text-slate-300 hover:text-red-500">
                                                    <Trash2 className="w-4 h-4"/>
                                                </button>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="bg-slate-50">
                                                <td colSpan={isSplitMode ? confirmedPeople.length + 6 : 8} className="p-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Notes</label>
                                                            <textarea
                                                                value={e.notes || ''}
                                                                onChange={ev => updateExpense(e.id, 'notes', ev.target.value)}
                                                                className="w-full border border-slate-200 rounded p-2 text-sm resize-none"
                                                                rows={3}
                                                                placeholder="Add notes about this expense..."
                                                            />
                                                        </div>
                                                        <div className="space-y-3">
                                                            <div className="flex items-center gap-3">
                                                                <label className="flex items-center gap-2 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={e.hasReceipt || false}
                                                                        onChange={ev => updateExpense(e.id, 'hasReceipt', ev.target.checked)}
                                                                        className="w-4 h-4 rounded text-emerald-600"
                                                                    />
                                                                    <span className="text-sm font-medium text-slate-700">Have Receipt?</span>
                                                                </label>
                                                            </div>
                                                            {!e.isSettlement && (
                                                                <div>
                                                                    <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Split With</label>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {confirmedPeople.map(p => {
                                                                            const isIncluded = !e.splitWith || e.splitWith.length === 0 || e.splitWith.includes(p.id);
                                                                            return (
                                                                                <button
                                                                                    key={p.id}
                                                                                    onClick={() => toggleExpenseSplit(e, p.id)}
                                                                                    className={`px-2 py-1 rounded text-xs font-medium ${
                                                                                        isIncluded
                                                                                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                                                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                                                                                    }`}
                                                                                >
                                                                                    {p.name}
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        <tr className="bg-emerald-50 font-bold border-t border-emerald-200">
                            <td className="p-3" colSpan="3"></td>
                            <td className="p-3 text-right text-emerald-700">${safeFixed(expenseSummary?.totalTripCost, 2)}</td>
                            <td colSpan={isSplitMode ? confirmedPeople.length + 2 : 4}></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
)}
                        {activeTab === 'settings' && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <SectionHeader title="Data Management & Settings" icon={Settings} color="slate" />
                               
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Download className="w-6 h-6"/></div>
                                            <div>
                                                <h3 className="font-bold text-slate-800">Backup Data</h3>
                                                <p className="text-xs text-slate-500">Download a JSON file containing all expedition data.</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-600 mb-6">
                                            Keep a local copy of your planning progress. You can use this file to restore your data on another device or revert to an earlier version.
                                        </p>
                                        <button
                                            onClick={downloadBackup}
                                            className="w-full flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold py-3 rounded-lg shadow-md hover:bg-indigo-700 transition-colors"
                                        >
                                            <FileJson className="w-5 h-5"/> Download .JSON Backup
                                        </button>
                                    </div>

                                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><Upload className="w-6 h-6"/></div>
                                            <div>
                                                <h3 className="font-bold text-slate-800">Restore Data</h3>
                                                <p className="text-xs text-slate-500">Import data from a previous JSON backup.</p>
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-600 mb-6">
                                            Select a backup file to merge with your current data. This will update existing items and add missing ones.
                                        </p>
                                       
                                        {isRestoring ? (
                                            <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 flex flex-col items-center justify-center animate-pulse">
                                                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mb-2"/>
                                                <span className="text-sm font-bold text-slate-700">{restoreStatus}</span>
                                            </div>
                                        ) : (
                                            <label className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 text-slate-600 font-bold py-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                                                <FileText className="w-5 h-5"/>
                                                <span>{restoreStatus || "Select Backup File"}</span>
                                                <input type="file" accept=".json" onChange={restoreFromBackup} className="hidden" />
                                            </label>
                                        )}
                                       
                                        <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg flex gap-3 items-start">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"/>
                                            <p className="text-[11px] text-amber-800">
                                                <strong>Warning:</strong> Restoring will overwrite items with the same IDs. This action cannot be undone once written to the cloud.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                    <h3 className="font-bold text-slate-800 mb-4">Planning Constants</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Food Weight Ratio</label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={foodLbsPerDay}
                                                    onChange={e => setFoodLbsPerDay(Number(e.target.value))}
                                                    className="w-24 border rounded-lg p-2 font-mono"
                                                />
                                                <span className="text-sm text-slate-600">lbs / person / day</span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-1 italic">Used for expedition load estimates.</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">App Environment</label>
                                            <div className="text-xs font-mono bg-slate-50 p-2 rounded border border-slate-100 break-all space-y-1">
                                                <div>ID: {appId}</div>
                                                <div>User UID: {user.uid}</div>
                                                <div>Status: Authenticated (Standard)</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </main>
        </div>
    );
}
